// Canvas 2D renderer. Reads the sim state every frame and keeps its own
// purely cosmetic state (floating words, sparks, shake) fed by sim events.

import type { Enemy, Judgement, Side, Sim, SimEvent } from '@/sim/sim';
import { TUNING as T } from '@/sim/tuning';
import { BOSSES, GRAPPLE, KICK, KINDS, type BossId, type EnemyKind } from '@/sim/tuning';
import { t } from '@/i18n';
import type { DojoStyle } from '@/content/dojos';
import {
  CHAMBER,
  KARATEKA,
  POSES,
  SHUKOKAI,
  STREET,
  UPPER,
  aimKick,
  aimPunch,
  blend,
  bounceDown,
  bodyFront,
  drawFighter,
  fit,
  footPoint,
  kickExtend,
  legReach,
  limbsOf,
  loadBossLooks,
  lunged,
  mixLimbs,
  nudge,
  pivotShift,
  reaching,
  rolled,
  smooth,
  stepBack,
  standHeight,
  steppedThrough,
  sweep,
  swing,
  type Build,
  type Eyes,
  type Joint,
  type KickPath,
  type Limb,
  type Pose,
  type Technique,
  type Vec,
} from './rig';
import { Backdrop, THEMES, drawAmbient, themeFor, type StageId, type Theme } from './stages';
import { Feet, gaitOf, rhythmFor, surgeAt, type Drive, type Gait } from './footwork';

export const C = {
  text: '#fff7ea',
  danger: '#ff4b3a',
  gold: '#ffc861',
  counter: '#8fe3ff',
  hurtEdge: 'rgba(255,40,30,0.55)',
};

// Silhouettes are told apart by their outline: build, clothes, stance
export const PLAYER_BUILD: Build = KARATEKA;

export const ENEMY_BUILDS: Record<EnemyKind, Build> = {
  // A plain rectangle, hood up: the yardstick for the others
  basic: { hair: 'hood', outfit: 'hoodie', bulk: 1, scale: 1, belly: 0.03 },
  // A pear: small bearded head, huge gut, short legs (in its poses)
  heavy: {
    hair: 'beard',
    outfit: 'bruiser',
    bulk: 1.5,
    scale: 1.2,
    shoulders: 0.92,
    back: 0.75,
    belly: 0.17,
    neck: 1.3,
    head: 0.9,
  },
  // The smallest and thinnest
  feinter: { hair: 'capBack', outfit: 'tracksuit', bulk: 0.8, scale: 0.92 },
  blocker: { hair: 'headband', outfit: 'boxer', bulk: 1.05, scale: 1.02, shoulders: 1.12 },
  // The tallest: a thin line on long legs
  kicker: { hair: 'mohawk', outfit: 'thai', bulk: 0.84, scale: 1.14 },
  // The widest, low as a wedge
  grappler: {
    hair: 'flattop',
    outfit: 'singlet',
    bulk: 1.4,
    scale: 1,
    shoulders: 1.2,
    neck: 1.9,
  },
};

/** The stage bosses, each an outline none of the regulars has: the dojo's
 *  senpai in a gi with a long black belt, the park's boss in a long coat and
 *  a wide hat, the rooftop's ronin in a hakama with a topknot. */
export const BOSS_BUILDS: Record<BossId, Build> = {
  senpai: { hair: 'bald', outfit: 'gi', bulk: 1.12, scale: 1.1, shoulders: 1.06, tails: 0.5 },
  captain: { hair: 'hat', outfit: 'coat', bulk: 1.1, scale: 1.12, shoulders: 1.15 },
  ronin: { hair: 'topknot', outfit: 'hakama', bulk: 1.05, scale: 1.1, shoulders: 1.08 },
};

/** A boss's own guard where his build calls for one: the senpai stands in kamae. */
export const BOSS_GUARD: Partial<Record<BossId, Pose>> = { senpai: SHUKOKAI.kamae };

/** Who's drawn: a regular by kind; a boss by his own build (his kind is his act). */
export function buildOf(e: { kind: EnemyKind; boss?: BossId | undefined }): Build {
  return e.boss ? BOSS_BUILDS[e.boss] : ENEMY_BUILDS[e.kind];
}

/** The guard an opponent comes in with: a boss keeps his own in his opening act's kind. */
export function guardOf(e: { kind: EnemyKind; boss?: BossId | undefined }): Pose {
  const own = e.boss ? BOSS_GUARD[e.boss] : undefined;
  return own && e.kind === BOSSES[e.boss!].acts[0] ? own : GUARD[e.kind];
}

export const EYE_CALM = '#f6eee0';

/** Everything needed to draw one fighter (and its reflection). */
export interface Figure {
  x: number; // world units
  y: number; // world units above the floor
  facing: 1 | -1;
  pose: Pose;
  build: Build;
  fill: string;
  eyes: Eyes;
  eyeColor: string;
  belt?: string;
  rim: string;
  rotation: number;
  alpha: number;
}

/**
 * One fighter rendered offscreen: `shape` is the solid silhouette tinted to
 * the rim colour, `full` adds belt and eyes, `lit` is the rim behind the full
 * figure, drawn as one image so a fading fighter never shows the rim through
 * its body. Compositing whole sprites avoids the light seams that per-part
 * anti-aliasing leaves at every joint.
 */
interface Sprite {
  shape: HTMLCanvasElement;
  full: HTMLCanvasElement;
  lit: HTMLCanvasElement;
  /** The rim shape shrunk to ~1/3 CSS px; scaled back up it's a soft glow. */
  halo: HTMLCanvasElement;
}

/** Sprite canvas size and feet position, in world units. */
const SPRITE_W = 3.4;
const SPRITE_H = 3.2;
const SPRITE_FEET = 0.8;

/**
 * How far a fighter's hips stand off the light source (world units) before its
 * rim shows on the light's side only; nearer, the far side's is partly lit too.
 */
const RIM_RAMP = 1;

/** White streak along the path of a strike. */
interface Slash {
  side: Side;
  kick: boolean;
  height: number;
  t: number;
  life: number;
  color: string;
}

/** The windup ring's radius (world units). */
const TELEGRAPH_R = 0.3;
/** How long a strike's slash shows, and the share of that its sweep takes. */
const SLASH_T = 0.16;
const SLASH_SWEEP = 1 / 2.2;

// The impact test's pad, its holder and its meter are drawn by impact-kit.ts,
// a chunk loaded when a match with the test starts
/** The impact meter's top (world units): its full-power zone. */
export const METER_TOP = 2.3;
/** Screen px the HUD's score and round lines take at the top. */
const HUD_H = 110;
/** The LED readout counts up to the reading over this long. */
export const COUNT_T = 0.55;
/** The punch into the pad stays out this long after the tap. */
const PAD_HELD = 0.5;
/** The pad's face (world units ahead of the karateka) as the senpai holds it
 *  at the right distance: where the gyaku-zuki's knuckles reach fully out. */
export const PAD_FACE = 0.77;

/** The senpai's step with the pad (world units off the right distance, +
 *  further away): from `from` to `to`, `u` of the way through (1: standing). */
export interface PadStep {
  from: number;
  to: number;
  u: number;
}

/** How the punch met the pad: at the right distance, jammed, or short of it. */
export type Maai = 'in' | 'close' | 'far';

/** The impact test as drawn: the meter, the reading once struck and seconds
 *  since, when the punch lands, the senpai's step and how the punch met the pad. */
export interface PadState {
  meter: number;
  value: number | null;
  t: number;
  lands: number;
  step: PadStep;
  maai: Maai | null;
}

/** How far off the right distance the pad (and the senpai's hips) are in `step`. */
export function padOffset(step: PadStep): number {
  return step.from + (step.to - step.from) * smooth(step.u);
}

/** When the impact test's gyaku-zuki reaches the pad `d` off the right distance
 *  (fully out if it falls short). */
export function padContact(d: number): number {
  return contactAt('gyakuZuki', undefined, PAD_FACE + d);
}

/** The impact test's pad and holder (impact-kit.ts), once loaded. */
let kit: typeof import('./impact-kit') | null = null;
let kitLoad: Promise<void> | null = null;

/** Load the impact test's pad and holder ahead of the test (once; retried if it failed). */
export function loadImpactKit(): Promise<void> {
  kitLoad ??= import('./impact-kit').then(
    (m) => void (kit = m),
    () => void (kitLoad = null),
  );
  return kitLoad;
}

export const TECHNIQUE_NAMES: Record<Technique, string> = {
  gyakuZuki: 'Gyaku-zuki',
  maekenZuki: 'Maeken-zuki',
  ipponAshiDachiZuki: 'Ippon-ashi-dachi-zuki',
  maeGeri: 'Mae-geri',
  mawashiGeri: 'Mawashi-geri',
  maeashiGeri: 'Maeashi-geri',
  maeashiGeriRear: 'Maeashi-geri',
};

/** The kicks aimed per opponent: mae-geri at the belly, maeashi-geri at the thigh. */
type Aimed = 'maeGeri' | 'maeashiGeri' | 'maeashiGeriRear';

type Unaimed = Exclude<Technique, Aimed>;

/** What strikes in each other technique: a fist or foot of its extended pose. */
const STRIKING: Record<Unaimed, Joint> = {
  gyakuZuki: 'bHand',
  maekenZuki: 'fHand',
  ipponAshiDachiZuki: 'bHand',
  mawashiGeri: 'bFoot',
};

/** The hit's flash and its head-or-body sound sit this far above that fist or foot. */
const LANDS_ABOVE = 0.16;

/**
 * Where each other technique lands (world units above the floor), read off
 * its own pose: a pose edited lower (a chudan gyaku-zuki) takes its hit down with it.
 */
function techniqueHeight(tech: Unaimed): number {
  return SHUKOKAI[tech][STRIKING[tech]][1] * PLAYER_BUILD.scale + LANDS_ABOVE;
}

/**
 * Mae-geri lands on the belly, between the pubic bone (level with the hip
 * joint) and the solar plexus (about 2/3 of the way up to the shoulders):
 * aimed at the middle, this far up from the hip joint.
 */
const BELLY = 0.33;

/**
 * Where a mae-geri lands on `kind` (world units above the floor): the middle
 * of the belly in its guard and in its stagger, the poses it shows when hit.
 */
export function maeGeriHeight(
  kind: EnemyKind,
  scale = ENEMY_BUILDS[kind].scale,
  pose = GUARD[kind],
): number {
  const belly = (p: Pose): number => p.hip[1] + BELLY * (p.shoulder[1] - p.hip[1]);
  return ((belly(pose) + belly(STAGGER[kind])) / 2) * scale;
}

/**
 * The part of `kind`'s front thigh (the leg nearer the player) a maeashi-geri
 * may land on, in its guard and its stagger alike (world units above the
 * floor): just above the knee up to just below the hip joint.
 */
export function thighSpan(
  kind: EnemyKind,
  scale = ENEMY_BUILDS[kind].scale,
  pose = GUARD[kind],
): [number, number] {
  const poses = [pose, STAGGER[kind]];
  const knee = Math.max(...poses.map((p) => p.fKnee[1]));
  const hip = Math.min(...poses.map((p) => p.hip[1]));
  return [(knee + 0.1 * (hip - knee)) * scale, (hip - 0.1 * (hip - knee)) * scale];
}

/** Maeashi-geri's shin meets the thigh this far down the straight kicking leg. */
const SHIN = 0.8;

/** Maeashi-geri's kicking leg as its pose sets it: the hip's height, the leg's length, how far it points down. */
function maeashiLeg(tech: 'maeashiGeri' | 'maeashiGeriRear'): {
  hy: number;
  len: number;
  down: number;
} {
  const p = SHUKOKAI[tech];
  const foot = tech === 'maeashiGeri' ? p.fFoot : p.bFoot;
  const [dx, dy] = [foot[0] - p.hip[0], p.hip[1] - foot[1]];
  return { hy: p.hip[1], len: Math.hypot(dx, dy), down: Math.atan2(dy, dx) };
}

/**
 * Where `tech` lands on `kind` (world units above the floor). Head-high
 * techniques come down to a low opponent's face (the crouching wrestler).
 */
export function strikeHeight(
  tech: Technique,
  kind: EnemyKind,
  scale = ENEMY_BUILDS[kind].scale,
  pose = GUARD[kind],
): number {
  if (tech === 'maeGeri') return maeGeriHeight(kind, scale, pose);
  if (tech === 'maeashiGeri' || tech === 'maeashiGeriRear') {
    // The shin lands where the pose points it; only where that would miss the
    // thigh (a tall opponent) does the leg tilt to its nearest part
    const { hy, len, down } = maeashiLeg(tech);
    const [lo, hi] = thighSpan(kind, scale, pose);
    return Math.min(hi, Math.max(lo, hy - SHIN * len * Math.sin(down)));
  }
  // No higher than the face of the opponent as it stands (the fist on it)
  return Math.min(techniqueHeight(tech), pose.head[1] * scale + LANDS_ABOVE);
}

/**
 * Where the striking fist or foot goes for a strike landing at `height`: a
 * kick's foot or a punch's fist, lowered as far as the strike comes down;
 * undefined keeps the technique's own line.
 */
export function aimFor(tech: Technique, height: number): number | undefined {
  if (tech === 'maeGeri') {
    // The ball of the foot lands: with the toes pulled back it sits above the ankle
    const pose = SHUKOKAI.maeGeri;
    return height - (footPoint(pose, PLAYER_BUILD, 'rear', 'ball')[1] - pose.bFoot[1]);
  }
  if (tech === 'maeashiGeri' || tech === 'maeashiGeriRear') {
    // The straight leg through its shin at `height`: the foot goes on past and below
    const { hy } = maeashiLeg(tech);
    return hy - (hy - height) / SHIN;
  }
  const drop = techniqueHeight(tech) - height;
  if (drop <= 0) return undefined;
  return SHUKOKAI[tech][STRIKING[tech]][1] - drop;
}

/** A strike this far below an opponent's head still lands on the head (chin, jaw). */
const HEAD_REACH = 0.2;

const KICKS = new Set<Technique>(['maeGeri', 'mawashiGeri', 'maeashiGeri', 'maeashiGeriRear']);

/** The punches thrown with the rear hand (the others with the lead hand). */
const REAR_ARM = new Set<Technique>(['gyakuZuki', 'ipponAshiDachiZuki']);

// Perfect timing earns the showier techniques; counters are the classic
// Shukokai gyaku-zuki; ordinary hits, which come with the opponent closer,
// are hand techniques and the low kick to the thigh off the lead leg. Each
// takes them in turn as far as they suit the distance (`suits()`).
const PERFECT_SET: Technique[] = [
  'mawashiGeri',
  'gyakuZuki',
  'maeGeri',
  'ipponAshiDachiZuki',
  'gyakuZuki',
  'maeashiGeriRear',
];
const GOOD_SET: Technique[] = [
  'maekenZuki',
  'gyakuZuki',
  'maeashiGeri',
  'gyakuZuki',
  'maekenZuki',
  'maeashiGeri',
];

/**
 * Seconds for a strike's chamber and extension, for holding it out and for
 * pulling it back: at half the speed they first had (the user's call).
 */
const LOAD_T = 0.04;
const CHAMBER_T = 0.08;
const EXTEND_T = 0.08;
const PUNCH_T = 0.09;
const HOLD_T = { punch: 0.15, kick: 0.04 };
export const RETRACT_T = 0.18;
/** Seconds the bounce in kamae takes to ease in after a technique. */
const BOUNCE_IN = 0.25;

/**
 * A technique's own clock from the tap (seconds), whatever the sim's strike
 * window: out and held until `held`, back in kamae by `back`.
 */
export function strikeSpan(tech: Technique): { held: number; back: number } {
  const held = contactTime(tech) + (CHAMBER[tech] ? HOLD_T.kick : HOLD_T.punch);
  return { held, back: held + RETRACT_T };
}

/** A strike starting while another still shows eases out of it this long. */
const BLEND_T = 0.06;

/** Front kicks unfold around the knee; the roundhouses sweep over it. */
const KICK_PATH: Record<Technique, KickPath> = {
  gyakuZuki: 'fold',
  maekenZuki: 'fold',
  ipponAshiDachiZuki: 'fold',
  maeGeri: 'fold',
  mawashiGeri: 'arc',
  maeashiGeri: 'arc',
  maeashiGeriRear: 'arc',
};

/**
 * What moves the rear leg forward: mae-geri, mawashi-geri and the rear
 * maeashi-geri kick with it, ippon-ashi-dachi-zuki punches while it steps through.
 */
export const REAR_LEG = new Set<Technique>([
  'maeGeri',
  'mawashiGeri',
  'maeashiGeriRear',
  'ipponAshiDachiZuki',
]);

/**
 * Rear-leg techniques land forward into the opposite stance a whole stance
 * ahead (body units), so the standing foot stays where it is; then that foot
 * steps back past it to its spot in kamae over STEP_BACK_T seconds.
 */
export const LAND_STEP = SHUKOKAI.kamae.fFoot[0] - SHUKOKAI.kamae.bFoot[0];
export const STEP_BACK_T = 0.36;
const LANDED = steppedThrough(SHUKOKAI.kamae, LAND_STEP);

/** The fist's knuckles sit this far past the hand joint, along the forearm. */
const FIST_FRONT = 0.09;

/**
 * What a technique strikes with as it lands (pose units, facing right, from
 * the karateka's spot): the knuckles, the ball of the foot, the instep, the shin.
 */
export function strikePoint(tech: Technique, aim: number | undefined, at = contactTime(tech)): Vec {
  const pose = strikePose(tech, at, aim);
  if (tech === 'maeGeri') return footPoint(pose, PLAYER_BUILD, 'rear', 'ball');
  if (tech === 'maeashiGeri' || tech === 'maeashiGeriRear') {
    const foot = tech === 'maeashiGeri' ? pose.fFoot : pose.bFoot;
    return [
      pose.hip[0] + (foot[0] - pose.hip[0]) * SHIN,
      pose.hip[1] + (foot[1] - pose.hip[1]) * SHIN,
    ];
  }
  if (tech === 'mawashiGeri') return pose.bFoot;
  return knuckles(pose, REAR_ARM.has(tech) ? 'bHand' : 'fHand');
}

/**
 * How far ahead of the karateka's spot a technique's fist or foot is `at`
 * seconds into it, by default fully out (world units).
 */
export function strikeReach(
  tech: Technique,
  aim: number | undefined,
  at = contactTime(tech),
): number {
  return strikePoint(tech, aim, at)[0] * PLAYER_BUILD.scale;
}

/**
 * When `tech` reaches a body whose front is `surface` ahead of the karateka
 * (world units): the first moment of its last, fast part that gets there, or
 * when it's fully out if it falls short.
 */
function contactAt(tech: Technique, aim: number | undefined, surface: number): number {
  const full = contactTime(tech);
  for (let t = CHAMBER[tech] ? full - EXTEND_T : 0; t < full; t += 0.004) {
    if (strikeReach(tech, aim, t) >= surface) return t;
  }
  return full;
}

/**
 * How far a strike goes into the body it lands on before it drives it back
 * (world units): a few centimetres, to hit with force (the user's call), but
 * maeashi-geri's shin, which slaps onto the thigh.
 */
function sinkOf(tech: Technique): number {
  return tech === 'maeashiGeri' || tech === 'maeashiGeriRear' ? 0 : 0.05;
}

/**
 * How far from the karateka an opponent of `build` in `pose` stands as `tech`
 * lands on it at `height` (world units): fully out, the fist or foot is as far
 * into its body there as the strike sinks.
 */
export function meetDistance(
  tech: Technique,
  build: Build,
  pose: Pose,
  aim: number | undefined,
): number {
  return strikeReach(tech, aim) - sinkOf(tech) + frontFor(tech, build, pose, aim);
}

/** Half the thickness of a fist or foot as it lands (world units). */
const STRIKE_BAND = 0.05;

/**
 * How far forward of its spot an opponent of `build` in `pose` meets `tech`
 * landing at `aim` (world units): its body's front at the fist's or foot's
 * own height, across its thickness (its spot if nothing's there).
 */
export function frontFor(
  tech: Technique,
  build: Build,
  pose: Pose,
  aim: number | undefined,
): number {
  const y = strikePoint(tech, aim)[1] * PLAYER_BUILD.scale;
  const front = bodyFront(pose, build, y / build.scale, STRIKE_BAND / build.scale);
  return Number.isFinite(front) ? front * build.scale : 0;
}

/**
 * The sim strikes an opponent at its reach (0.86), but the drawn techniques
 * reach further: up close, opponents are drawn this much further away (where
 * a gyaku-zuki fully out has sunk into a street fighter)…
 */
const STAND_OFF = 0.11;
/** …easing in from the edge of a portrait phone's mat, so they come into view where they did. */
const STAND_OFF_FROM = 2.5;

/** Where an opponent `d` from the karateka in the sim is drawn (world units). */
export function drawnDistance(d: number): number {
  const u = Math.max(0, Math.min(1, (STAND_OFF_FROM - d) / (STAND_OFF_FROM - T.reach)));
  return d + STAND_OFF * u * u * (3 - 2 * u);
}

/** At most this far does an opponent step in to meet a strike that would fall short of it. */
const MEET_MAX = 0.6;
/** How far a boxing glove's padding reaches past the hand (body units). */
const GLOVE_FRONT = 0.1;

/** How far inside the edge of reach (drawn) the techniques off the rear leg still suit. */
const EDGE_SLACK = 0.03;
/** How far inside the body a fist or foot may be as its strike's last, fast part starts. */
const INSIDE_MAX = 0.1;

/**
 * Whether `tech` suits an opponent of `kind` and `build` in `pose`, drawn `at`
 * from the karateka (the user saw kicks too close). Those off the rear leg
 * reach the furthest, further than any opponent stands when hit, so they
 * touch early and shove: they come only with the opponent at the edge of
 * reach, where a well-timed hit catches it. And none comes with its fist or
 * foot already well inside the body as it starts its last, fast part (a
 * kick's snap, a punch from kamae): mae-geri and mawashi-geri on bodies as
 * wide as the bruiser's or the wrestler's, the lead fist on one up close.
 */
export function suits(
  tech: Technique,
  kind: EnemyKind,
  build: Build,
  pose: Pose,
  at: number,
): boolean {
  if (REAR_LEG.has(tech) && at < drawnDistance(T.reach) - EDGE_SLACK) return false;
  const aim = aimFor(tech, strikeHeight(tech, kind, build.scale, pose));
  const starts = CHAMBER[tech] ? contactTime(tech) - EXTEND_T : 0;
  return strikeReach(tech, aim, starts) <= at - frontFor(tech, build, pose, aim) + INSIDE_MAX;
}

/** Seconds from the start of a strike until it lands (fully extended). */
export function contactTime(tech: Technique): number {
  return CHAMBER[tech] ? LOAD_T + CHAMBER_T + EXTEND_T : PUNCH_T;
}

/**
 * How far from the karateka the strike `meet` waits for keeps its body `since`
 * the hit (world units): as far as the fist or foot reaches, less what it
 * sinks in, until it's pulled back (0 then).
 */
function clearOf(meet: Meet, since: number): number {
  if (since >= strikeSpan(meet.tech).held) return 0;
  const at = Math.min(since, contactTime(meet.tech));
  return strikeReach(meet.tech, meet.aim, at) - sinkOf(meet.tech) + meet.front;
}

/** A technique's extended pose; a kick's foot or a punch's fist lands at height `aim` if given. */
function techniquePose(tech: Technique, aim?: number): Pose {
  const pose = SHUKOKAI[tech];
  if (aim === undefined) return pose;
  if (!CHAMBER[tech]) return aimPunch(pose, aim, REAR_ARM.has(tech) ? 'rear' : 'front');
  return aimKick(pose, aim, REAR_LEG.has(tech) ? 'rear' : 'front');
}

/** At the earliest, a punch's feet are done sliding this far into it. */
const SLID_FROM = 0.3;

/** A punch's hips and body have done their part this far into it, ahead of the fist. */
const HIPS_LEAD = 0.8;

/**
 * The player's pose `tt` seconds into a strike (a kick's foot landing at
 * `aim`); a punch's sliding feet arrive as it `lands` on the body (seconds in).
 */
export function strikePose(tech: Technique, tt: number, aim?: number, lands = PUNCH_T): Pose {
  // Kicks pass through their chamber; punches snap straight out
  const target = techniquePose(tech, aim);
  const chamber = CHAMBER[tech];
  if (!chamber) {
    // Punches: the hips turn and drive first, from rest, and the fist follows
    // them out, fastest as it lands
    const u = Math.min(1, tt / PUNCH_T);
    const body = smooth(u / HIPS_LEAD);
    const fist = u * u;
    const hand = REAR_ARM.has(tech) ? 'bHand' : 'fHand';
    const pose = blend(SHUKOKAI.kamae, target, body);
    pose[hand] = mix(SHUKOKAI.kamae[hand], target[hand], fist);
    slideFeet(
      tech,
      pose,
      SHUKOKAI.kamae,
      target,
      u,
      Math.max(SLID_FROM, Math.min(1, lands / PUNCH_T)),
    );
    const limbs = mixLimbs(limbsOf(SHUKOKAI.kamae), limbsOf(target), body);
    const arm = hand === 'bHand' ? 2 : 3;
    limbs[arm] = mixLimbs(limbsOf(SHUKOKAI.kamae), limbsOf(target), fist)[arm]!;
    return planted(tech, reaching(pose, limbs));
  }
  // Knees bend first (load), then the knee comes up (chamber), then the
  // lower leg snaps out from the knee; each part starts and settles smoothly
  const load = SHUKOKAI.kickLoad;
  const up = (tt - LOAD_T) / CHAMBER_T;
  if (tt < LOAD_T) return planted(tech, swing(SHUKOKAI.kamae, load, smooth(tt / LOAD_T)));
  if (up < 1) return planted(tech, swing(load, chamber, smooth(up)));
  const t = (tt - LOAD_T - CHAMBER_T) / EXTEND_T;
  const leg = REAR_LEG.has(tech) ? 'rear' : 'front';
  return planted(tech, kickExtend(chamber, target, t, KICK_PATH[tech], leg));
}

/** How far short of an opponent's body a strike too early stops (world units). */
const WHIFF_GAP = 0.12;

/** The fist's knuckles in `pose` (pose units). */
function knuckles(pose: Pose, arm: 'fHand' | 'bHand'): Vec {
  const [hand, elbow] = [pose[arm], arm === 'bHand' ? pose.bElbow : pose.fElbow];
  const a = Math.atan2(hand[1] - elbow[1], hand[0] - elbow[0]);
  return [hand[0] + Math.cos(a) * FIST_FRONT, hand[1] + Math.sin(a) * FIST_FRONT];
}

/** The shoulders and arms, which lean on after a strike too early. */
const LEANS: readonly Joint[] = ['shoulder', 'head', 'fElbow', 'fHand', 'bElbow', 'bHand'];
/** How far the shoulders lean on after it (body units). */
const OVERREACH = 0.08;
/** A strike too early stays out as long as the sim leaves the player open, then comes back. */
const WHIFF_SPAN = { held: T.whiffRecover, back: T.whiffRecover + RETRACT_T };

/**
 * Striking too early: a gyaku-zuki `t` seconds out into the air, the shoulders
 * leaning on after it, but never further than `reach` (world units ahead):
 * short of the opponent it was too early for, it's only partly out. The legs
 * are the gyaku-zuki's own at every moment (the user saw an overlong stance
 * of its own fold oddly).
 */
export function whiffPose(t: number, reach = Infinity): Pose {
  const full = contactTime('gyakuZuki');
  const at = (u: number): Pose =>
    nudge(strikePose('gyakuZuki', u), LEANS, OVERREACH * smooth(u / full), 0);
  return punchTo(at, Math.min(t, full), reach);
}

/** The impact test's gyaku-zuki `t` seconds in, stopping as its fist reaches
 *  the pad's face `face` ahead (world units): fully out at the right distance,
 *  with the arm still bent on a pad too close. */
function padPunch(t: number, face: number): Pose {
  const at = (u: number): Pose => strikePose('gyakuZuki', u);
  return punchTo(at, Math.min(t, contactTime('gyakuZuki')), face);
}

/** The gyaku-zuki `at(t)` `t` seconds in, or at the moment its fist first got
 *  `reach` ahead (world units), if it did. */
function punchTo(at: (u: number) => Pose, t: number, reach: number): Pose {
  const fist = (q: Pose): number => knuckles(q, 'bHand')[0] * PLAYER_BUILD.scale;
  const out = at(t);
  if (fist(out) <= reach) return out;
  let [lo, hi] = [0, t];
  for (let i = 0; i < 14; i++) {
    const u = (lo + hi) / 2;
    if (fist(at(u)) <= reach) lo = u;
    else hi = u;
  }
  return at(lo);
}

/**
 * Pulling a technique back (`k` 1 → 0), easing out of it and into kamae: a
 * kick refolds to the knee before it sets down; a rear-leg technique sets the
 * foot down in front (ending in LANDED).
 */
export function retractPose(tech: Technique, k: number, aim?: number): Pose {
  const chamber = CHAMBER[tech];
  const target = techniquePose(tech, aim);
  const end = REAR_LEG.has(tech) ? LANDED : SHUKOKAI.kamae;
  if (!chamber) {
    const u = smooth(1 - k);
    const pose = blend(target, end, u);
    slideFeet(tech, pose, target, end, u, 1, true);
    return planted(tech, reaching(pose, mixLimbs(limbsOf(target), limbsOf(end), u)));
  }
  // Snapped back to the knee, settling there an instant, then set down
  if (k > 0.5) return planted(tech, swing(target, chamber, smooth((1 - k) / 0.5)));
  return planted(tech, swing(chamber, end, smooth((0.5 - k) / 0.5)));
}

const mix = (a: Vec, b: Vec, t: number): Vec => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
];

/**
 * The feet that slide along the floor as a punch goes out, each over its own
 * part of the way (from, to), the other way round as it comes back: gyaku-zuki
 * and maeken-zuki start with a brief slide of the front foot, and maeken-zuki's
 * is a double slide, the rear foot sliding up after it (the user's calls).
 */
const SLIDES: Partial<Record<Technique, [foot: 'fFoot' | 'bFoot', from: number, to: number][]>> = {
  gyakuZuki: [['fFoot', 0, 0.5]],
  maekenZuki: [
    ['fFoot', 0, 0.5],
    ['bFoot', 0.4, 1],
  ],
};

/**
 * Moves `pose`'s sliding feet `u` (0 → 1) of the way from `a`'s to `b`'s,
 * each over its own part of the way, on the floor, done by `slid` of it (as the
 * fist lands); going `back`, the rear foot slides first.
 */
function slideFeet(
  tech: Technique,
  pose: Pose,
  a: Pose,
  b: Pose,
  u: number,
  slid = 1,
  back = false,
): void {
  const v = Math.min(1, u / slid);
  for (const [foot, from, to] of SLIDES[tech] ?? []) {
    const [start, end] = back ? [1 - to, 1 - from] : [from, to];
    const w = Math.max(0, Math.min(1, (v - start) / (end - start)));
    // The first foot eases off; one following it snaps in, landing as the fist does
    pose[foot] = mix(a[foot], b[foot], start > 0 && !back ? w * w : smooth(w));
  }
}

/**
 * The foot a technique stands on, planted where kamae has it (pose units):
 * the front foot for the rear-leg techniques, the rear foot for maeashi-geri
 * off the lead leg (no skip in).
 */
function standing(tech: Technique): { foot: 'fFoot' | 'bFoot'; at: number } | null {
  if (REAR_LEG.has(tech)) return { foot: 'fFoot', at: SHUKOKAI.kamae.fFoot[0] };
  if (tech === 'maeashiGeri') return { foot: 'bFoot', at: SHUKOKAI.kamae.bFoot[0] };
  return null;
}

const ALL_JOINTS: readonly Joint[] = [...UPPER, 'fKnee', 'fFoot', 'bKnee', 'bFoot'];

/**
 * A standing foot that's down stays where it's planted: where a pose sets it
 * elsewhere, the whole fighter shifts instead of the foot sliding. One turning
 * on its ball keeps the ball there, the ankle and the body going round with
 * the heel (`pivotShift`).
 */
function planted(tech: Technique, pose: Pose): Pose {
  const s = standing(tech);
  if (!s) return pose;
  const [x, y] = pose[s.foot];
  if (y > 0.002) return pose;
  const at = s.at + (s.foot === 'fFoot' && pose.pivot ? pivotShift(pose.pivot, PLAYER_BUILD) : 0);
  return x === at ? pose : nudge(pose, ALL_JOINTS, at - x, 0);
}

/** How long an opponent's landed attack shows (sim seconds), and the share of it held out. */
const ATTACK_T = 0.5;
const ATTACK_HELD = 0.2;
/**
 * How long before it lands each type's attack is thrown (sim seconds): the
 * boxer's cross as fast as the karateka's punch, the haymaker and the hammer
 * blow swinging a longer way round.
 */
const THROW_T: Record<EnemyKind, number> = {
  basic: 0.13,
  heavy: 0.14,
  feinter: 0.1,
  blocker: 0.09,
  kicker: 0, // his kick flies in after the windup
  grappler: 0.11,
};
/** How far an attack lunges, across the stand-off, to reach the karateka. */
const LUNGE = 0.22 + STAND_OFF;
/** Sim seconds the kickboxer's kick flies in for, chambered outside reach to landing. */
const LAUNCH_T = (T.reach + KICK.stand - T.attackRange) / KICK.launchSpeed;
/** Sim seconds a knocked-down opponent is in the air, from the strike reaching it. */
export const AIRBORNE = 0.3;
/** How far a knocked-down opponent tips back over its flight (radians about the hips). */
const FALL_TURN = 1.25;
/** How high it bounces as it lands on its back, and for how long (world units, sim seconds). */
const BOUNCE = 0.05;
const BOUNCE_T = 0.16;

/** `pose` turned `a` radians about its hips; turned forward, its head goes back. */
function turned(pose: Pose, a: number): Pose {
  const [hx, hy] = pose.hip;
  const [c, s] = [Math.cos(a), Math.sin(a)];
  const out = { ...pose };
  for (const j of ALL_JOINTS) {
    const [dx, dy] = [pose[j][0] - hx, pose[j][1] - hy];
    out[j] = [hx + dx * c - dy * s, hy + dx * s + dy * c];
  }
  return out;
}

/** Flat on the back as it lands, turned back up by the tip its flight gives it. */
const LANDING = turned(STREET.down, -FALL_TURN);

/**
 * A knocked-down opponent `u` (0–1) of the way through its flight, reeling
 * from `stagger`: lifted off its feet, tipping back (the head going away from
 * the karateka), the hips coming down to land it flat on its back, as
 * `STREET.down`. `rotation` turns the drawing about the hips, `lift` raises it.
 */
export function knockedDown(
  stagger: Pose,
  u: number,
): { pose: Pose; rotation: number; lift: number } {
  return {
    pose: sweep(stagger, LANDING, u),
    rotation: FALL_TURN * u,
    lift: Math.sin(u * Math.PI) * 0.35,
  };
}

/** How an opponent is moving, as drawn: the sim's state, or the renderer's own reading of it. */
type Mode = Enemy['state'] | 'meet' | 'attack' | 'stand' | 'back';
/** Ways of moving an opponent snaps into: held still as a strike is on its way. */
const SNAPS: ReadonlySet<Mode> = new Set(['meet']);
/** How much of the sim's footwork pulse a walking opponent's pace keeps. */
const PULSE_KEPT = 0.15;
/** Seconds an opponent eases from how it was drawn into a new way of moving;
 *  as a strike lands on it, quicker. */
const EASE_T = 0.1;
const EASE_STRUCK = 0.05;
/** Seconds a kicking leg struck as it came in takes to come down. */
const KICK_DOWN_T = 0.2;
/** Each leg: its foot's index in the footwork, the foot, the knee. */
const LEGS = [
  [0, 'fFoot', 'fKnee'],
  [1, 'bFoot', 'bKnee'],
] as const;

/** How an opponent a strike reached reels: from when, how far into its stagger, held and
 *  recovering how long (sim seconds). */
interface Reel {
  at: number;
  amount: number;
  hold: number;
  back: number;
}
/** Seconds a strike takes to rock an opponent back. */
const REEL_IN = 0.07;
/** The reel of a plain hit; a boss knocked out of an act reels longer. */
const REELS = {
  hit: { amount: 1, hold: 0.1, back: 0.45 },
  act: { amount: 1, hold: 0.2, back: 0.5 },
  // The wrestler barely flinches before pressing on; the boxer takes it on the gloves
  shrug: { amount: 0.4, hold: 0.04, back: 0.25 },
  blocked: { amount: 0.3, hold: 0.06, back: 0.3 },
} as const;

/** How far into its stagger an opponent is (0–1) `time` (sim seconds), reeling `r`. */
function reeling(r: Reel, time: number): number {
  const t = time - r.at;
  if (t < 0) return 0;
  return (
    r.amount * (t < REEL_IN ? smooth(t / REEL_IN) : 1 - smooth((t - REEL_IN - r.hold) / r.back))
  );
}

/** How briskly a knocked-back opponent's drawn place follows the sim's (radians a second,
 *  critically damped), and for how long after the knockback, fading out. */
const GLIDE_W = 20;
const GLIDE_T = 0.5;
/** The fastest a strike still going out shoves an opponent on (world units a second). */
const SHOVE_MAX = 6;

/** Stepping in less than this to meet a strike (world units), an opponent's feet stay put. */
const STILL_MEET = 0.02;
/** A foot this high in a pose (body units) is off the floor (a kick): the pose's own to move. */
const GROUNDED = 0.1;
/** The feinter's stamp: out, then back (sim seconds each). */
const FEINT_STAMP = 0.11;
/** Walking upright, the hips this share of the legs' length up: the standing knee bent
 *  some 15 degrees, as a person's is walking (a fighting crouch's is 50). */
const STRAIGHT = 0.985;
/** How far the hips may come down for the feet to stay on the floor (body units). */
const MAX_DROP = 0.2;

/**
 * How far a foot the walk rolls onto its ball (`gait`'s heels, rolled through)
 * brings its ankle on and up beyond where the pose's own heel (`own`) has it
 * (body units): how much further back its leg then reaches.
 */
function rollOf(build: Build, gait: Gait, own: number): Vec {
  const [a, b] = [rolled(build, gait.heel + 0.6), rolled(build, own)];
  return [Math.max(0, a[0] - b[0]), Math.max(0, a[1] - b[1])];
}

/**
 * How far the body may go past a planted foot (world units) for its leg to
 * reach it all the way, `build` in `guard`, walking as `gait` does: what a
 * walk's steps are kept to (footwork.ts, rhythmFor).
 */
export function reachSpan(guard: Pose, build: Build, gait: Gait): number {
  const span = Math.min(...reachesOf(guard, build, gait).map(([lo, hi]) => hi - lo));
  return span * 0.9;
}

/** About half an arm's length (body units). */
const ARM_HALF = 0.3;

/**
 * `pose` with an arm (`f` the lead, `b` the rear) swung `by` body units at the
 * hand, turned about its shoulder so it keeps its length.
 */
function swungArm(pose: Pose, side: 'f' | 'b', by: number): Pose {
  const [elbow, hand] =
    side === 'f' ? (['fElbow', 'fHand'] as const) : (['bElbow', 'bHand'] as const);
  const [sx, sy] = pose.shoulder;
  // (an arm folded, its hand at the shoulder, turns no more than one half out)
  const reach = Math.max(ARM_HALF, Math.hypot(pose[hand][0] - sx, pose[hand][1] - sy));
  const a = by / reach;
  const [c, sn] = [Math.cos(a), Math.sin(a)];
  // (turned clockwise, the way it faces, the hand goes forward and down)
  const turn = ([x, y]: Vec): Vec => [
    sx + (x - sx) * c + (y - sy) * sn,
    sy - (x - sx) * sn + (y - sy) * c,
  ];
  return { ...pose, [elbow]: turn(pose[elbow]), [hand]: turn(pose[hand]) };
}

/** How long `guard`'s legs are (world units, `k` the build's scale), the shorter's. */
export function legsOf(guard: Pose, k: number): number {
  const limbs = limbsOf(guard);
  return Math.min(limbs[0]![0] + limbs[0]![1], limbs[1]![0] + limbs[1]![1]) * k;
}

/** How far behind and ahead of the body each of `pose`'s feet may land (world units). */
export function reachesOf(
  pose: Pose,
  build: Build,
  gait: Gait,
): readonly [readonly [number, number], readonly [number, number]] {
  return [reached(pose, 'fFoot', build, gait), reached(pose, 'bFoot', build, gait)];
}

/** How far behind and ahead of the body `pose`'s `foot` may land (world units). */
function reached(pose: Pose, foot: 'fFoot' | 'bFoot', build: Build, gait: Gait): [number, number] {
  const k = build.scale;
  const limbs = limbsOf(pose);
  const [, hi] = legReach(pose, limbs, foot, MAX_DROP / 2);
  // (left behind, it rolls onto its ball: its ankle comes up and on)
  const roll = rollOf(build, gait, (foot === 'bFoot' ? pose.heel : pose.fHeel) ?? 0);
  const [lo] = legReach(pose, limbs, foot, MAX_DROP / 2, roll[1]);
  return [(lo - roll[0]) * k, hi * k];
}

/**
 * `pose` (its body at `u`, world units along the way it faces; `k` its
 * build's scale) stood on the feet `f` has on the floor at `time`: a foot
 * shoved further than its leg reaches drags along, the hips come down as far
 * as the legs need. Walking (`walk`, 0–1: how much), in `gait`'s own way: it
 * straightens up out of its crouch as far as the legs allow (highest over the
 * standing foot, lower as the feet pass apart), rises with a step, leans into
 * the pace and sways on with each step, the hands swinging against the legs,
 * and each foot rolls up onto its ball as it's left behind and leaves the
 * floor toe last.
 */
export function feetUnder(
  f: Feet,
  pose: Pose,
  u: number,
  build: Build,
  time: number,
  gait: Gait,
  walk: number,
  running = false,
): Pose {
  const k = build.scale;
  const limbs = limbsOf(pose);
  let out: Pose = { ...pose };
  const heels = [pose.fHeel ?? 0, pose.heel ?? 0];
  // Where each leg reaches to: the ankle, as the walk rolls the foot onto its
  // ball beyond the pose's own (the foot itself stays on the floor)
  const shift: [Vec, Vec] = [
    [0, 0],
    [0, 0],
  ];
  for (const [i, foot] of [
    [0, 'fFoot'],
    [1, 'bFoot'],
  ] as const) {
    if (pose[foot][1] > GROUNDED) {
      // The pose's own (a kick), its lower leg eased into from where the
      // foot stood
      const [dx, dy] = f.loose(i, time);
      const knee = foot === 'fFoot' ? 'fKnee' : 'bKnee';
      const [kx, ky] = f.looseKnee(i, time, [pose[knee][0] * k, pose[knee][1] * k]);
      out[foot] = [pose[foot][0] + dx / k, pose[foot][1] + dy / k];
      out[knee] = [pose[knee][0] + kx / k, pose[knee][1] + ky / k];
      continue;
    }
    const [fu, fy] = f.where(i, time);
    let fx = (fu - u) / k;
    if (walk > 0) {
      // Rolling up onto the ball as the body goes on past it, off the floor
      // toe last, flat again as it swings through
      const through = f.through(i, time);
      const behind = smooth((-fx - 0.08) / 0.3);
      // (in the air, from as far as it had rolled on the floor)
      const roll = through === null ? behind : behind * (1 - smooth(through / 0.4));
      const own = heels[i]!;
      heels[i] = own + (gait.heel + 0.6 * roll - own) * walk;
      const [a, b] = [rolled(build, heels[i]), rolled(build, own)];
      shift[i] = [a[0] - b[0], a[1] - b[1]];
    }
    // (one stepping is carried along too: its leg would stretch to it, or
    // bring the hips down to the floor, when a strike shoves the body away)
    const [lo, hi] = legReach(out, limbs, foot, MAX_DROP);
    const kept = Math.max(lo, Math.min(hi, fx + shift[i][0])) - shift[i][0];
    if (kept !== fx) {
      f.drag(i, u + kept * k, time);
      fx = kept;
    }
    // (on the floor at the height the pose has it: on its ball, say)
    out[foot] = [fx, pose[foot][1] + fy / k];
  }
  out = { ...out, heel: heels[1]!, ...(heels[0] ? { fHeel: heels[0] } : {}) };
  let rise = 0;
  if (walk > 0) {
    const s = f.stepping(time);
    if (s) {
      // The hands swing against the legs, the upper body sways on with the step
      const a = gait.swing * walk * Math.sin(Math.PI * s.k) * (s.i === 1 ? 1 : -1);
      out = swungArm(swungArm(out, 'f', a), 'b', -a);
      out = nudge(out, UPPER.slice(1), gait.sway * walk * Math.sin(Math.PI * s.k), 0);
    }
    // Up out of the guard's lean, leaning into the pace instead
    const stoop = Math.max(0, out.shoulder[0] - out.hip[0]) * gait.torso * walk;
    const lean = gait.lean * walk * smooth(f.v / 2.5) - stoop;
    if (lean) out = nudge(out, UPPER.slice(1), lean, 0);
    // Up out of the crouch toward near-straight legs, as far as they reach
    const legs = Math.min(limbs[0]![0] + limbs[0]![1], limbs[1]![0] + limbs[1]![1]);
    // (running, the knees stay bent more: the hips low and steady)
    rise = Math.max(0, STRAIGHT * legs - out.hip[1]) * gait.upright * walk * (running ? 0.5 : 1);
    rise += gait.bounce * walk * f.rise(time);
  }
  // Standing: the feet the footwork has down, on their balls too; the knees
  // placed for the legs to reach the ankles, then the feet back on the floor
  const up = (i: 0 | 1, foot: 'fFoot' | 'bFoot'): boolean =>
    pose[foot][1] > GROUNDED || (f.swinging(i, time) && f.where(i, time)[1] > 0.003 * k);
  const reach: Pose = {
    ...out,
    fFoot: [out.fFoot[0] + shift[0][0], out.fFoot[1] + shift[0][1]],
    bFoot: [out.bFoot[0] + shift[1][0], out.bFoot[1] + shift[1][1]],
  };
  const free = { fFoot: up(0, 'fFoot'), bFoot: up(1, 'bFoot') };
  let height = standHeight(reach, limbs, rise, free);
  // The hips come down toward where a stepping foot will have them as it
  // lands, as it reaches the floor, not all at once as it touches it
  for (const [i, foot] of [
    [0, 'fFoot'],
    [1, 'bFoot'],
  ] as const) {
    const land = f.landing(i, time);
    if (!land || pose[foot][1] > GROUNDED) continue;
    const there: Pose = { ...reach, [foot]: [(land.at - u) / k, pose[foot][1]] };
    const low = standHeight(there, limbs, rise, { ...free, [foot]: false });
    height += Math.min(0, low - height) * smooth((land.k - 0.65) / 0.35);
  }
  const dy = f.hips(height, time);
  // (the legs only: the arms are as the pose and the swing have them)
  // (the legs only, and of those not one a kick lifts: the arms and a
  // kicking leg are as the pose has them, going up or down with the hips;
  // re-solved, a chambered knee or a near-straight elbow flipped over)
  const kicking = (i: number): boolean => pose[i === 0 ? 'bFoot' : 'fFoot'][1] > GROUNDED;
  const legs = limbs.map((l, i): Limb => (i < 2 && !kicking(i) ? l : [l[0], l[1], NaN]));
  const lifted = (['fKnee', 'fFoot', 'bKnee', 'bFoot'] as const).filter((j) =>
    kicking(j[0] === 'b' ? 0 : 1),
  );
  const moved = dy ? nudge(nudge(reach, UPPER, 0, dy), lifted, 0, dy) : reach;
  const fitted = fit(moved, legs);
  f.knees = [
    [fitted.fKnee[0] * k, fitted.fKnee[1] * k],
    [fitted.bKnee[0] * k, fitted.bKnee[1] * k],
  ];
  return {
    ...fitted,
    fFoot: [fitted.fFoot[0] - shift[0][0], fitted.fFoot[1] - shift[0][1]],
    bFoot: [fitted.bFoot[0] - shift[1][0], fitted.bFoot[1] - shift[1][1]],
  };
}

export const GUARD: Record<EnemyKind, Pose> = {
  basic: STREET.basicGuard,
  heavy: STREET.heavyGuard,
  feinter: STREET.feinterGuard,
  blocker: STREET.blockerGuard,
  kicker: STREET.kickerGuard,
  grappler: STREET.grapplerGuard,
};
export const WINDUP: Record<EnemyKind, Pose> = {
  basic: STREET.basicWindup,
  heavy: STREET.heavyWindup,
  feinter: STREET.feinterWindup,
  blocker: STREET.blockerWindup,
  kicker: STREET.kickerWindup,
  grappler: STREET.grapplerWindup,
};
const ATTACK: Record<EnemyKind, Pose> = {
  basic: STREET.basicAttack,
  heavy: STREET.heavyAttack,
  feinter: STREET.feinterAttack,
  blocker: STREET.blockerAttack,
  kicker: STREET.kickerAttack,
  grappler: STREET.grapplerAttack,
};
/** Rocked by a hit, in each build's own proportions (short legs, long legs, the low wedge). */
export const STAGGER: Record<EnemyKind, Pose> = {
  basic: STREET.stagger,
  heavy: nudge(nudge(STREET.stagger, UPPER, 0, -0.08), ['fKnee', 'bKnee'], 0, -0.06),
  feinter: STREET.stagger,
  blocker: STREET.stagger,
  kicker: nudge(nudge(STREET.stagger, UPPER, 0, 0.08), ['fKnee', 'bKnee'], 0, 0.05),
  grappler: STREET.grapplerStagger,
};
/** The two-hit types after the first hit: one more lands it. */
export const HURT: Partial<Record<EnemyKind, Pose>> = {
  heavy: STREET.heavyHurt,
  grappler: STREET.grapplerHurt,
};

/**
 * Each type's own idle motion over its guard, walking or waiting: the
 * bruiser rolls his shoulders, the feinter's loose hands swing, the boxer
 * bobs, the kickboxer springs on his toes, the wrestler hand-fights. The
 * elbows and knees follow (`fit`), so no arm or leg changes its length.
 */
function idle(kind: EnemyKind, p: Pose, t: number): Pose {
  switch (kind) {
    case 'heavy':
      return nudge(p, UPPER.slice(1), Math.sin(t * 3) * 0.025, Math.cos(t * 3) * 0.012);
    case 'feinter': {
      const q = nudge(p, ['fHand'], 0, Math.sin(t * 6) * 0.035);
      return fit(nudge(q, ['bHand'], 0, Math.sin(t * 6 + 2) * 0.03), limbsOf(p));
    }
    case 'blocker': {
      const dip = (0.5 + 0.5 * Math.sin(t * 3.5)) * 0.05;
      const q = nudge(p, UPPER.slice(1), Math.sin(t * 3.5) * 0.02, -dip);
      return fit(nudge(q, ['hip'], 0, -dip * 0.6), limbsOf(p));
    }
    case 'kicker':
      return { ...p, heel: (p.heel ?? 0) + 0.2 * Math.sin(t * 9.6) };
    case 'grappler': {
      const q = nudge(p, ['fHand'], Math.sin(t * 5) * 0.05, 0);
      return fit(nudge(q, ['bHand'], -Math.sin(t * 5) * 0.05, 0), limbsOf(p));
    }
    default:
      return p;
  }
}

/**
 * An opponent in guard, backlit and filling `canvas` (sized by CSS): the
 * portrait on the exam intro card, so players know the shape before the fight.
 */
export function drawPortrait(canvas: HTMLCanvasElement, who: EnemyKind | BossId): void {
  const dpr = Math.min(3, window.devicePixelRatio || 1);
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  canvas.width = Math.round(w * dpr);
  canvas.height = Math.round(h * dpr);
  const g = canvas.getContext('2d');
  if (!g || !w || !h) return;
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  const glow = g.createRadialGradient(w / 2, h * 0.45, 0, w / 2, h * 0.45, h * 0.62);
  glow.addColorStop(0, 'rgba(255, 232, 184, 0.6)');
  glow.addColorStop(1, 'rgba(255, 200, 97, 0)');
  g.fillStyle = glow;
  g.fillRect(0, 0, w, h);
  const boss = who in BOSS_BUILDS ? (who as BossId) : undefined;
  const look = { kind: boss ? BOSSES[boss].acts[0]! : (who as EnemyKind), boss };
  const pose = guardOf(look);
  const build = buildOf(look);
  // Scaled to fill the frame: the shape matters here, not the size
  const xs = Object.values(pose).flatMap((v) => (Array.isArray(v) ? [v[0] as number] : []));
  const left = Math.min(...xs) - 0.16;
  const right = Math.max(...xs) + 0.18;
  const ppu = Math.min(
    (h - 12) / ((pose.head[1] + 0.2) * build.scale),
    (w - 6) / ((right - left) * build.scale),
  );
  const x = w / 2 - ((left + right) / 2) * ppu * build.scale;
  const ground = h - 5;
  const rim = Math.max(1.2, ppu * 0.025);
  const paint = { eyes: 'calm' as const, eyeColor: EYE_CALM, time: 0.3 };
  drawFighter(g, x + rim, ground - rim * 1.1, ppu, 1, pose, build, { ...paint, fill: C.gold });
  drawFighter(g, x, ground, ppu, 1, pose, build, { ...paint, fill: THEMES.park.ink });
}

interface Floater {
  x: number; // world units (signed)
  y: number;
  text: string;
  sub?: string;
  color: string;
  t: number;
  life: number;
  /** Text height in text units (`tu`). */
  size: number;
  /** World units the word drifts up over its life (default 0.5). */
  rise?: number;
}

/** Star-shaped impact flash at the point of contact. */
interface Impact {
  x: number;
  y: number;
  t: number;
  life: number;
  size: number;
  color: string;
}

interface Spark {
  x: number;
  y: number;
  vx: number;
  vy: number;
  t: number;
  life: number;
  color: string;
  /** Ink droplet (a round blot) rather than a streak of light. */
  ink?: boolean;
}

/** Expanding shockwave ring on the strongest hits. */
interface Ring {
  x: number;
  y: number;
  t: number;
  life: number;
  size: number;
  color: string;
}

/**
 * An opponent from the hit until the strike lands: still as it stood, settling
 * from where it was drawn to where the strike meets its body.
 */
interface Meet {
  pose: Pose;
  eyes: Eyes;
  eyeColor: string;
  from: number;
  to: number;
  /** How far its body's front stands ahead of it at the strike's height (world units). */
  front: number;
  tech: Technique;
  aim: number | undefined;
  /** Sim time of the hit. */
  at: number;
  /** Knocked down: how much further a strike still going out has shoved it than its flight. */
  shove: number;
}

export class Renderer {
  private readonly ctx: CanvasRenderingContext2D;
  private w = 0;
  private h = 0;
  private dpr = 1;
  private ppu = 50;
  /** Text unit for calls and banners: the world's scale, capped on short
   *  screens (landscape, desktop), where it would make words 30 % of the height. */
  private tu = 50;
  private groundY = 0;
  private floaters: Floater[] = [];
  private sparks: Spark[] = [];
  // Not private, nor are rings, zoom, shake, bannerText, float() and burst():
  // the impact kit sets them off too (padLanded())
  impacts: Impact[] = [];
  private slashes: Slash[] = [];
  rings: Ring[] = [];
  // What's kept per opponent is keyed by the opponent itself, never its id:
  // every match numbers its opponents from 1 and its clock from 0 again, and
  // a retry's opponents once inherited the last try's strikes, held invisible
  // (the user saw). The sim keeps each as one object while it's on the mat.
  /** Seconds of white hit-flash left. */
  private flashes = new Map<Enemy, number>();
  /** Camera punch-in on perfect hits, decays to 0. */
  zoom = 0;
  private readonly backdrop = new Backdrop();
  /** Renderer clock (real seconds) for ambience and fluttering cloth. */
  private time = 0;
  private sprites: Sprite[] = [];
  /** Offscreen layer the floor reflections are faded in. */
  private floorLayer: HTMLCanvasElement | null = null;
  /** When the last strike reaches its opponent (seconds after the hit); null: once fully out. */
  private lastHold: number | null = null;
  /** Opponents struck and waiting for the strike to land. */
  private meets = new WeakMap<Enemy, Meet>();
  /** Where each opponent was last drawn (world x), for its marks. */
  private drawnX = new WeakMap<Enemy, number>();
  /** How each opponent was last drawn: what a strike then meets. */
  private shown = new WeakMap<Enemy, { pose: Pose; x: number; eyes: Eyes; eyeColor: string }>();
  /** The technique the player is showing (chosen when the hit registers). */
  private technique: Technique = 'gyakuZuki';
  /** Test hook: every hit shows this technique when set (screenshots). */
  pinTechnique: Technique | null = null;

  /** Whether the last strike shown was a kick (for the sound of it). */
  get lastWasKick(): boolean {
    return KICKS.has(this.technique);
  }
  /** Sim seconds from the last strike shown until it reaches the opponent. */
  get lastContact(): number {
    return this.lastHold ?? contactTime(this.technique);
  }
  /** The opponent the last strike landed on, and its head's height as it stood. */
  private struck: EnemyKind = 'basic';
  private struckHead = GUARD.basic.head[1];
  /** Where the last strike landed (world units above the floor). */
  private struckHeight = techniqueHeight('gyakuZuki');
  /** How high the last kick's foot lands, if aimed at the opponent. */
  private aim: number | undefined;
  /** Sim seconds from the hit until the strike reaches each opponent struck. */
  private contact = new WeakMap<Enemy, number>();
  /**
   * Whether the last strike landed on the head (else the body): the same
   * technique reaches a short opponent's head but a tall one's chest.
   */
  get lastWasHead(): boolean {
    return this.struckHeight >= this.struckHead - HEAD_REACH;
  }
  /** The PERFECT and the GOOD hits' techniques, in the order they come up. */
  private perfectTurn = [...PERFECT_SET];
  private goodTurn = [...GOOD_SET];
  /** Sim time the technique showing started at, and the side it went to. */
  strikeAt = -Infinity;
  /** Whether what's showing is a strike too early (else `technique`). */
  private whiffed = false;
  /** How long what's showing is out and when it's back. */
  private get span(): { held: number; back: number } {
    return this.whiffed ? WHIFF_SPAN : strikeSpan(this.technique);
  }
  /** Whether it then steps back from in front. */
  private get stepsBack(): boolean {
    return !this.whiffed && REAR_LEG.has(this.technique);
  }
  /** Sim time the technique showing is over by, the karateka back in kamae. */
  get settled(): number {
    return this.strikeAt + this.span.back + (this.stepsBack ? STEP_BACK_T : 0);
  }
  private strikeSide: Side = 1;
  /** The player as last drawn, and as drawn when the technique showing began. */
  private lastPlayer: { pose: Pose; x: number; facing: Side } | null = null;
  private strikeFrom: { pose: Pose; x: number; facing: Side } | null = null;
  /** Effects waiting for a strike to reach its target (sim time). */
  private later: { at: number; run: () => void }[] = [];
  /** Each landed attack: when (sim time) and where it started. */
  private attacks = new WeakMap<Enemy, { at: number; x: number }>();
  /** When the player last took a hit (sim time): those backed off then aren't struck. */
  private hurtAt = -Infinity;
  /** Each opponent's feet on the floor. */
  private feet = new WeakMap<Enemy, Feet>();
  /** Each opponent a strike reached, reeling from it. */
  private reels = new WeakMap<Enemy, Reel>();
  /** Each knocked-back opponent: where it's drawn, how fast it's going (world units), when,
   *  and till when it glides. */
  private glides = new WeakMap<Enemy, { x: number; v: number; t: number; until: number }>();
  /** Each walking opponent: its walk (when it set off), where the sim had it then if it had
   *  walked freely all along, how much of its pulse is taken out and when that was. */
  private walks = new WeakMap<Enemy, { began: number; d0: number; even: number; t: number }>();
  /** Each opponent's distance at the last sim step drawn, and whether that step moved it. */
  private steps = new WeakMap<Enemy, { d: number; at: number; moving: boolean }>();
  /** How each opponent is moving, since when, the pose it eases from and the one last drawn. */
  private modes = new WeakMap<
    Enemy,
    {
      mode: Mode;
      at: number;
      /** The sim's state and how long it had been in it, last frame. */
      state: Enemy['state'];
      stateT: number;
      from: Pose | null;
      /** Easing from standing still for a strike: which feet the footwork has. */
      held: [boolean, boolean] | null;
      /** How far from where it now stands it was drawn when the mode changed. */
      off: number;
      last: Pose;
      lastX: number;
    }
  >();
  stage: StageId = 'dojo';
  /** Impact test: meter position, the reading once struck, time since the strike… */
  pad: PadState | null = null;
  shake = 0;
  private hurtFlash = 0;
  private kiaiFlash = 0;
  bannerText: { title: string; sub: string; t: number; life: number } | null = null;
  /** The player's current belt, tied around the gi. */
  beltColor = '#f5f5f0';
  beltEdge = '#c9c6bc';
  /** Dojo name for the sign above the mat; null shows the generic sign. */
  sign: string | null = null;
  /** Which dojo room the dojo stage shows: the player's dojo. */
  dojoStyle: DojoStyle = 'kamiza';

  private get theme(): Theme {
    return themeFor(this.stage, this.dojoStyle);
  }
  /** Tap rings for a first-time player, or one who swiped (0–1); fades with each hit. */
  tapHint = 0;
  /** What the coach shows (app/coach.ts): the side to strike now, the side an
   *  opponent comes in on (its tap ring lights up), the new opponent's word. */
  coach: { now: Side | 0; near: Side | 0; label: Enemy | null } = {
    now: 0,
    near: 0,
    label: null,
  };

  constructor(private readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas 2D unavailable');
    this.ctx = ctx;
    this.resize();
  }

  /** Resolution cap, lowered by the game if frames run slow. */
  maxDpr = 2;
  /** Player asked for less motion: no shake or zoom, gentler flashes. */
  reducedMotion = false;

  resize(): void {
    this.dpr = Math.min(this.maxDpr, window.devicePixelRatio || 1);
    this.w = this.canvas.clientWidth;
    this.h = this.canvas.clientHeight;
    this.canvas.width = Math.round(this.w * this.dpr);
    this.canvas.height = Math.round(this.h * this.dpr);
    // Fit ±2.5 units across a portrait screen (enemies fade in from spawn) across the width; landscape is height-bound
    this.ppu = Math.min(this.w / 4.8, this.h / 4.4);
    this.tu = Math.min(this.ppu, this.h * 0.15);
    // Portrait keeps the fighters low, near the thumbs, with room above for calls
    this.groundY = this.h * (this.w < this.h ? 0.7 : 0.82);
  }

  /** Clear leftover effects when a new match starts. */
  reset(): void {
    this.floaters = [];
    this.sparks = [];
    this.shake = 0;
    this.hurtFlash = 0;
    this.kiaiFlash = 0;
    this.bannerText = null;
    this.impacts = [];
    this.slashes = [];
    this.rings = [];
    this.flashes.clear();
    this.attacks = new WeakMap();
    this.hurtAt = -Infinity;
    this.later = [];
    this.strikeAt = -Infinity;
    this.strikeFrom = null;
    this.whiffed = false;
    this.zoom = 0;
    // A retry shows the same techniques at the same moments
    this.perfectTurn = [...PERFECT_SET];
    this.goodTurn = [...GOOD_SET];
    this.technique = 'gyakuZuki';
    this.lastHold = null;
    this.aim = undefined;
  }

  /** A technique starts at sim time `time` toward `side`, easing out of what showed. */
  private startStrike(time: number, side: Side, whiff = false): void {
    this.whiffed = whiff;
    this.strikeFrom = this.lastPlayer;
    this.strikeAt = time;
    this.strikeSide = side;
  }

  /** Big centered call ("Round 2 / Hajime!") shown for `life` seconds. */
  banner(title: string, sub: string, life: number): void {
    this.bannerText = { title, sub, t: 0, life };
  }

  /** Cosmetic reactions to what the sim just did. */
  onEvents(events: SimEvent[], time = 0): void {
    // A boss's act ends with the hit that follows it in the batch
    const acts: (() => void)[] = [];
    for (const ev of events) {
      switch (ev.type) {
        case 'hit': {
          const x = ev.enemy.side * drawnDistance(ev.enemy.d) * 0.8;
          // Aimed at the opponent as it stands (in guard, winding up, hurt…)
          const build = buildOf(ev.enemy);
          const was = this.shown.get(ev.enemy);
          const stood = was?.pose ?? guardOf(ev.enemy);
          const from = was?.x ?? ev.enemy.side * drawnDistance(ev.enemy.d);
          const tech =
            this.pinTechnique ??
            this.techniqueFor(ev.judgement, ev.enemy.kind, build, stood, Math.abs(from));
          this.technique = tech;
          this.struck = ev.enemy.kind;
          const kick = KICKS.has(tech);
          const move = TECHNIQUE_NAMES[tech];
          const height = strikeHeight(tech, ev.enemy.kind, build.scale, stood);
          this.struckHeight = height;
          this.struckHead = stood.head[1] * build.scale;
          this.aim = aimFor(tech, height);
          // The strike lands as it reaches the opponent's body (which holds
          // still till then) and drives it back as it finishes; one that would
          // fall short has the opponent step in to meet it
          const front = frontFor(tech, build, stood, this.aim);
          // How far out the strike sinks in
          const out = strikeReach(tech, this.aim) - sinkOf(tech);
          // Where its body meets the strike: where it stands if the strike gets
          // there, else as far in as it may step
          const stands = Math.abs(from) - front;
          const surface = Math.max(Math.min(out, stands), stands - MEET_MAX);
          this.lastHold = contactAt(tech, this.aim, surface);
          this.startStrike(time, ev.enemy.side);
          this.contact.set(ev.enemy, this.lastHold);
          this.meets.set(ev.enemy, {
            pose: stood,
            eyes: was?.eyes ?? 'calm',
            eyeColor: was?.eyeColor ?? EYE_CALM,
            from,
            to: ev.enemy.side * (surface + front),
            front,
            tech,
            aim: this.aim,
            at: time,
            shove: 0,
          });
          // Rocked back as it lands (a boss knocked into his next act: further)
          if (!ev.killed) {
            const how = events.some((a) => a.type === 'act' && a.enemy === ev.enemy)
              ? REELS.act
              : ev.enemy.kind === 'grappler'
                ? REELS.shrug
                : REELS.hit;
            this.reels.set(ev.enemy, { at: time + this.lastHold, ...how });
          }
          const reach = ev.enemy.side * surface;
          // From three in a row, the judgement carries the PERFECT chain
          const label =
            ev.chain >= 3 && ev.judgement !== 'good'
              ? t(ev.judgement === 'counter' ? 'fight.counterChain' : 'fight.perfectChain', {
                  count: ev.chain,
                })
              : t(`fight.${ev.judgement}`);
          const color =
            ev.judgement === 'good' ? C.text : ev.judgement === 'counter' ? C.counter : C.gold;
          // The slash sweeps in as the strike goes out, reaching it as it lands
          const slash = { side: ev.enemy.side, kick, height, t: 0, life: SLASH_T, color };
          this.later.push({
            at: time + Math.max(0, this.lastHold - SLASH_T * SLASH_SWEEP),
            run: () => this.slashes.push(slash),
          });
          // The hit registers as the strike gets there: its call, the flash,
          // the sparks and the shake
          this.later.push({
            at: time + this.lastHold,
            run: () => {
              this.float({
                x,
                y: 2.1,
                text: label,
                // The technique's name; the points show in the score
                sub: move,
                color,
                life: 0.8,
                size: ev.judgement === 'good' ? 0.3 : 0.4,
              });
              this.burst(x, 1.2, ev.killed ? 14 : 7, color);
              this.impacts.push({
                x: reach,
                y: height - 0.05,
                t: 0,
                life: 0.14,
                size: ev.judgement === 'good' ? 0.32 : 0.5,
                color: ev.judgement === 'good' ? '#ffffff' : color,
              });
              this.flashes.set(ev.enemy, 0.07);
              if (ev.judgement !== 'good') {
                this.rings.push({ x: reach, y: height - 0.05, t: 0, life: 0.2, size: 0.75, color });
                this.zoom = 0.045;
              }
              if (ev.killed) {
                this.inkSplash(ev.enemy.side * (surface + front), height, ev.enemy.side);
              }
              this.shake = Math.max(this.shake, ev.killed ? 0.06 : 0.03);
              // Each new tier of the PERFECT chain is called out
              if (ev.chain > 0 && ev.chain % T.chain.step === 0) {
                this.float({
                  x: 0,
                  y: 2.9,
                  text: t('fight.chainMilestone', { count: ev.chain }),
                  color: C.gold,
                  life: 1.1,
                  size: 0.34,
                });
              }
            },
          });
          this.tapHint = Math.max(0, this.tapHint - 0.25);
          break;
        }
        case 'blocked': {
          // A jab into the boxer's gloves: he stands still till it gets
          // there (stepping in to meet it if it would fall short), then it
          // pushes him back, rocking the gloves
          const e = ev.enemy;
          const tech = 'maekenZuki';
          const was = this.shown.get(e);
          const stood = was?.pose ?? guardOf(e);
          const from = was?.x ?? e.side * drawnDistance(e.d);
          const k = buildOf(e).scale;
          const front = (Math.max(stood.fHand[0], stood.bHand[0]) + GLOVE_FRONT) * k;
          const out = strikeReach(tech, undefined);
          const stands = Math.abs(from) - front;
          const surface = Math.max(Math.min(out, stands), stands - MEET_MAX);
          this.technique = tech;
          this.lastHold = contactAt(tech, undefined, surface);
          this.startStrike(time, e.side);
          this.contact.set(e, this.lastHold);
          this.meets.set(e, {
            pose: stood,
            eyes: was?.eyes ?? 'calm',
            eyeColor: was?.eyeColor ?? EYE_CALM,
            from,
            to: e.side * (surface + front),
            front,
            tech,
            aim: undefined,
            at: time,
            shove: 0,
          });
          this.reels.set(e, { at: time + this.lastHold, ...REELS.blocked });
          const x = e.side * drawnDistance(e.d);
          this.later.push({
            at: time + this.lastHold,
            run: () => {
              this.float({
                x,
                y: 2.0,
                text: t('fight.blocked'),
                sub: t('fight.blockedHint'),
                color: '#c9d6e8',
                life: 0.8,
                size: 0.3,
              });
              this.burst(x * 0.8, 1.3, 5, '#c9d6e8');
            },
          });
          break;
        }
        case 'whiff':
          // Out into the air, easing out of whatever showed
          this.startStrike(time, ev.side, true);
          this.float({
            x: ev.side * 0.9,
            y: 1.9,
            text: t('fight.tooEarly'),
            color: '#bbb',
            life: 0.6,
            size: 0.24,
          });
          break;
        case 'hurt':
          this.float({
            x: ev.enemy.side * 0.9,
            y: 1.9,
            text: t('fight.tooLate'),
            color: C.danger,
            life: 0.7,
            size: 0.24,
          });
          this.strikeAt = -Infinity;
          this.hurtAt = time;
          this.attacks.set(ev.enemy, {
            at: time,
            x: ev.enemy.side * drawnDistance(ev.enemy.d),
          });
          this.shake = 0.14;
          this.hurtFlash = 1;
          this.burst(0, 1.2, 10, C.danger);
          break;
        case 'act': {
          // A boss knocked out of one act and into the next: a burst of ink,
          // as the strike gets there if one brought it, and a longer reel
          const at = ev.enemy.side * drawnDistance(ev.enemy.d);
          // (one a strike brought reels as it lands: the hit's own)
          if (!events.some((h) => h.type === 'hit' && h.enemy === ev.enemy)) {
            this.reels.set(ev.enemy, { at: time, ...REELS.act });
          }
          const run = (): void => {
            this.inkSplash(at, 1.2, ev.enemy.side);
            this.shake = Math.max(this.shake, 0.06);
          };
          if (events.some((h) => h.type === 'hit')) acts.push(run);
          else run();
          break;
        }
        case 'kiai':
          // Everyone goes down at once, no strike to wait for
          this.contact = new WeakMap();
          this.meets = new WeakMap();
          this.kiaiFlash = 1;
          this.shake = 0.16;
          this.float({
            x: 0,
            y: 2.5,
            text: t('fight.kiai'),
            color: C.gold,
            life: 1.2,
            size: 0.7,
          });
          break;
        default:
          break;
      }
    }
    for (const run of acts) this.later.push({ at: time + this.lastContact, run });
  }

  /**
   * The technique a hit shows on an opponent standing `at`: a counter's
   * gyaku-zuki, else the judgement's next that suits the distance; one that
   * doesn't waits at the front of the line for a hit it suits.
   */
  private techniqueFor(
    judgement: Judgement,
    kind: EnemyKind,
    build: Build,
    pose: Pose,
    at: number,
  ): Technique {
    if (judgement === 'counter') return 'gyakuZuki';
    const turn = judgement === 'perfect' ? this.perfectTurn : this.goodTurn;
    const i = turn.findIndex((tech) => suits(tech, kind, build, pose, at));
    const [tech = 'gyakuZuki'] = i < 0 ? [] : turn.splice(i, 1);
    if (i >= 0) turn.push(tech);
    return tech;
  }

  /** The punch got to the pad: its flash, shake and call (the impact kit's). */
  impactLanded(value: number, max: number, maai: Maai): void {
    if (this.pad) kit?.padLanded(this, value, max, maai, padOffset(this.pad.step));
  }

  /** Stack words that pop at nearly the same moment instead of overprinting. */
  float(f: Omit<Floater, 't'>): void {
    // Each fresh call pushes the next one above its word and subline
    const lift =
      (this.floaters
        .filter((o) => o.t < 0.35)
        .reduce((sum, o) => sum + o.size + (o.sub ? 0.45 : 0.15), 0) *
        this.tu) /
      this.ppu;
    this.floaters.push({ ...f, y: f.y + lift, t: 0 });
  }

  burst(x: number, y: number, n: number, color: string): void {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const v = 1.5 + Math.random() * 3;
      this.sparks.push({
        x,
        y,
        vx: Math.cos(a) * v,
        vy: Math.sin(a) * v + 1,
        t: 0,
        life: 0.35 + Math.random() * 0.2,
        color,
      });
    }
  }

  /** A knockout throws a few drops of ink off the far side, sumi-e style. */
  private inkSplash(x: number, y: number, side: Side): void {
    const ink = this.theme.ink;
    for (let i = 0; i < 7; i++) {
      const a = (Math.random() - 0.5) * 1.4 + (side > 0 ? 0.3 : Math.PI - 0.3);
      const v = 2 + Math.random() * 3;
      this.sparks.push({
        x,
        y,
        vx: Math.cos(a) * v,
        vy: Math.sin(a) * v + 1.5,
        t: 0,
        life: 0.45 + Math.random() * 0.2,
        color: ink,
        ink: true,
      });
    }
  }

  draw(sim: Sim, realDt: number): void {
    const due = this.later.filter((l) => l.at <= sim.state.time);
    if (due.length) {
      this.later = this.later.filter((l) => l.at > sim.state.time);
      for (const l of due) l.run();
    }
    const { ctx } = this;
    const s = sim.state;
    this.tickFx(realDt);

    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    const view = {
      w: this.w,
      h: this.h,
      groundY: this.groundY,
      ppu: this.ppu,
      dpr: this.dpr,
      sign: this.sign ?? t('fight.dojoSign'),
      style: this.dojoStyle,
    };
    this.backdrop.draw(ctx, this.stage, view);
    this.time += realDt;
    drawAmbient(ctx, this.stage, view, this.time, this.backdrop.lights);

    ctx.save();
    if (this.zoom > 0) {
      // Punch in around the fighters' chests
      const cx = this.w / 2;
      const cy = this.sy(1);
      ctx.translate(cx, cy);
      ctx.scale(1 + this.zoom, 1 + this.zoom);
      ctx.translate(-cx, -cy);
    }
    if (this.shake > 0) {
      const m = this.shake * this.ppu;
      ctx.translate((Math.random() - 0.5) * m, (Math.random() - 0.5) * m);
    }

    // Enemies back to front so the nearest overlaps, the pad's holder behind
    // the pad, the player on top
    const figures = [...s.enemies]
      .sort((a, b) => b.d - a.d)
      .map((e) => this.enemyFigure(e, s.time));
    if (this.pad && kit) figures.push(kit.holderFigure(this.pad, this.theme));
    figures.push(this.playerFigure(sim));
    const sprites = figures.map((f, i) => this.renderSprite(f, i));
    if (this.theme.reflect) this.drawReflections(figures, sprites);
    if (this.tapHint > 0 || this.coach.near) this.drawTapHint();
    for (const f of figures) {
      this.drawShadow(f.x, 0.42 * f.build.scale * f.build.bulk ** 0.5, f.alpha);
    }
    const last = figures.length - 1;
    for (let i = 0; i < last; i++) this.drawFigure(figures[i]!, sprites[i]!);
    for (const e of s.enemies) this.drawEnemyMarks(e, s.time);
    const pad = this.pad &&
      kit && {
        ctx,
        ppu: this.ppu,
        sx: (x: number) => this.sx(x),
        sy: (y: number) => this.sy(y),
        rim: this.rimPx(),
        theme: this.theme,
      };
    if (pad) kit!.drawPad(pad, this.pad!);
    for (const sl of this.slashes) this.drawSlash(sl, figures[last]!);
    this.drawFigure(figures[last]!, sprites[last]!);
    if (pad) kit!.drawMeter(pad, this.pad!);

    this.drawSparks();
    ctx.globalCompositeOperation = this.theme.blend;
    for (const im of this.impacts) this.drawImpact(im);
    for (const r of this.rings) this.drawRing(r);
    ctx.globalCompositeOperation = 'source-over';
    ctx.restore();

    // Vignette over the scene, calls and scores above it (steady, unshaken)
    this.backdrop.drawVignette(ctx, view);
    for (const f of this.floaters) this.drawFloater(f);
    if (this.coach.now) this.drawCoachCue(this.coach.now);

    if (this.hurtFlash > 0) this.drawHurt();
    if (this.bannerText) this.drawBanner(this.bannerText);
    if (this.kiaiFlash > 0) this.drawKiai();
  }

  /** Damage: the screen's edges bleed red, the fighters stay in view. */
  private drawHurt(): void {
    const { ctx, w, h } = this;
    const r = Math.hypot(w, h) / 2;
    const g = ctx.createRadialGradient(w / 2, h / 2, r * 0.45, w / 2, h / 2, r);
    g.addColorStop(0, 'rgba(255,40,30,0)');
    g.addColorStop(1, C.hurtEdge);
    ctx.globalAlpha = this.hurtFlash;
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    ctx.globalAlpha = 1;
  }

  /** Kiai: light bursts out from the player with speed lines, then a soft wash. */
  private drawKiai(): void {
    const { ctx, w, h, ppu } = this;
    const k = this.kiaiFlash;
    const cx = this.sx(0);
    const cy = this.sy(1.2);
    const r = Math.max(w, h) * (0.35 + (1 - k) * 0.5);
    ctx.save();
    ctx.globalCompositeOperation = this.theme.blend;
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    g.addColorStop(0, 'rgba(255,248,225,0.9)');
    g.addColorStop(0.35, 'rgba(255,214,140,0.35)');
    g.addColorStop(1, 'rgba(255,214,140,0)');
    ctx.globalAlpha = k * (this.reducedMotion ? 0.5 : 1);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    if (!this.reducedMotion && k > 0.3) {
      // Speed lines, re-rolled every frame so they flicker outwards
      ctx.strokeStyle = '#fff6e0';
      ctx.lineCap = 'round';
      for (let i = 0; i < 28; i++) {
        const a = (i / 28) * Math.PI * 2 + Math.random() * 0.2;
        const r0 = ppu * (1.4 + Math.random() * 0.8);
        const r1 = r0 + ppu * (0.8 + Math.random() * 1.6);
        ctx.lineWidth = ppu * (0.02 + Math.random() * 0.03);
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0);
        ctx.lineTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
        ctx.stroke();
      }
    }
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = '#fff';
    ctx.globalAlpha = k * k * 0.25;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }

  /** Streaks of light along each spark's flight; ink drops are round blots. */
  private drawSparks(): void {
    const { ctx, ppu } = this;
    ctx.lineCap = 'round';
    for (const sp of this.sparks) {
      if (!sp.ink) continue;
      const k = 1 - sp.t / sp.life;
      ctx.fillStyle = sp.color;
      ctx.globalAlpha = Math.min(1, k * 2);
      ctx.beginPath();
      ctx.arc(this.sx(sp.x), this.sy(sp.y), 0.045 * ppu * (0.6 + k * 0.6), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalCompositeOperation = this.theme.blend;
    for (const sp of this.sparks) {
      if (sp.ink) continue;
      const k = 1 - sp.t / sp.life;
      const x = this.sx(sp.x);
      const y = this.sy(sp.y);
      ctx.strokeStyle = sp.color;
      ctx.globalAlpha = k;
      ctx.lineWidth = 0.05 * ppu * (0.4 + k);
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x - sp.vx * 0.035 * ppu, y + sp.vy * 0.035 * ppu);
      ctx.stroke();
    }
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
  }

  private drawRing(r: Ring): void {
    const { ctx, ppu } = this;
    const k = r.t / r.life;
    const ease = 1 - (1 - k) ** 3;
    ctx.globalAlpha = 1 - k;
    ctx.strokeStyle = r.color;
    ctx.lineWidth = ppu * 0.07 * (1 - k) + 1;
    ctx.beginPath();
    ctx.arc(this.sx(r.x), this.sy(r.y), r.size * ppu * (0.2 + ease * 0.8), 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  private tickFx(dt: number): void {
    this.shake = Math.max(0, this.shake - dt * 0.6);
    this.hurtFlash = Math.max(0, this.hurtFlash - dt * 2.5);
    this.kiaiFlash = Math.max(0, this.kiaiFlash - dt * 2);
    this.zoom = Math.max(0, this.zoom - dt * 0.35);
    if (this.reducedMotion) {
      this.shake = 0;
      this.zoom = 0;
    }
    for (const r of this.rings) r.t += dt;
    this.rings = this.rings.filter((r) => r.t < r.life);
    for (const im of this.impacts) im.t += dt;
    this.impacts = this.impacts.filter((im) => im.t < im.life);
    for (const sl of this.slashes) sl.t += dt;
    this.slashes = this.slashes.filter((sl) => sl.t < sl.life);
    for (const [e, left] of this.flashes) {
      if (left - dt <= 0) this.flashes.delete(e);
      else this.flashes.set(e, left - dt);
    }
    if (this.bannerText) {
      this.bannerText.t += dt;
      if (this.bannerText.t >= this.bannerText.life) this.bannerText = null;
    }
    for (const f of this.floaters) f.t += dt;
    this.floaters = this.floaters.filter((f) => f.t < f.life);
    for (const sp of this.sparks) {
      sp.t += dt;
      sp.x += sp.vx * dt;
      sp.y += sp.vy * dt;
      sp.vy -= 9 * dt;
    }
    this.sparks = this.sparks.filter((sp) => sp.t < sp.life);
  }

  private sx(x: number): number {
    return this.w / 2 + x * this.ppu;
  }

  private sy(y: number): number {
    return this.groundY - y * this.ppu;
  }

  /** How far out the tap hints sit (world units): in each tap half, wide
   *  screens putting them clear of the Kiai bar. */
  private hintOut(): number {
    return Math.max(1.35, this.w / 4 / this.ppu);
  }

  /** The tap rings' height on screen (px): on the floor, a little above its
   *  middle, so they stay clear of the coach's tips over the Kiai bar. */
  private hintY(): number {
    return this.groundY + (this.h - this.groundY) * 0.45;
  }

  /** The tap rings' radius (px): big enough to read as a thumb's target. */
  private hintR(): number {
    return this.ppu * 0.2;
  }

  /** The coach's NOW!: over the tap half to strike, above its tap ring. */
  private drawCoachCue(side: Side): void {
    const { ctx, tu } = this;
    const x = this.sx(side * this.hintOut());
    const y = this.hintY() - this.hintR() * 1.15 - tu * 0.2;
    const size = tu * 0.42 * (1 + 0.08 * Math.sin(this.time * 14));
    ctx.font = `900 ${Math.round(size)}px system-ui, sans-serif`;
    this.outlined(t('coach.now'), x, y, C.gold);
  }

  /** Text in the calls' style: a solid ink outline, so it reads on every stage. */
  private outlined(text: string, x: number, y: number, color: string): void {
    const { ctx, tu } = this;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    ctx.lineWidth = tu * 0.1;
    ctx.strokeStyle = 'rgba(20,14,18,0.9)';
    ctx.strokeText(text, x, y);
    ctx.fillStyle = color;
    ctx.fillText(text, x, y);
  }

  /** A tap ring in each half, on the floor: tap here (a dot in a ring, a
   *  ripple going out of it as from a fingertip; arrows read as "swipe"). */
  private drawTapHint(): void {
    const { ctx, ppu } = this;
    const y = this.hintY();
    const r = this.hintR();
    ctx.save();
    ctx.strokeStyle = ctx.fillStyle = this.theme.rim;
    ctx.lineWidth = ppu * 0.05;
    const out = this.hintOut();
    for (const side of [-1, 1] as Side[]) {
      // The side the coach has an opponent coming in on lights up, rippling quicker
      const lit = side === this.coach.near;
      const alpha = lit
        ? 0.75 + 0.25 * Math.sin(this.time * 9)
        : this.tapHint * (0.3 + 0.2 * Math.sin(this.time * 4));
      const x = this.sx(side * out);
      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.arc(x, y, r * 0.35, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.stroke();
      const ripple = (this.time * (lit ? 1.6 : 0.9)) % 1;
      ctx.globalAlpha = alpha * (1 - ripple);
      ctx.beginPath();
      ctx.arc(x, y, r * (1 + 0.7 * ripple), 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  }

  /**
   * How far a strike too early may reach toward `side` (world units): short of
   * the nearest opponent's body there as it's drawn now, at the fist's height.
   */
  private whiffReach(sim: Sim, side: Side): number {
    const y = strikePoint('gyakuZuki', undefined)[1] * PLAYER_BUILD.scale;
    let reach = Infinity;
    for (const e of sim.state.enemies) {
      const x = this.drawnX.get(e);
      const shown = this.shown.get(e);
      if (e.side !== side || e.state === 'dead' || x === undefined || !shown) continue;
      const build = buildOf(e);
      const front = bodyFront(shown.pose, build, y / build.scale, STRIKE_BAND / build.scale);
      const body = Math.abs(x) - (Number.isFinite(front) ? front * build.scale : 0);
      reach = Math.min(reach, body - WHIFF_GAP);
    }
    return reach;
  }

  private playerFigure(sim: Sim): Figure {
    const p = sim.state.player;
    let pose: Pose;
    // Impact test: a full gyaku-zuki into the pad, held while the reading
    // counts, then pulled back
    const punch = this.pad?.value != null ? this.pad.t : -1;
    // The technique showing runs on its own clock from the tap: out, held,
    // pulled back and, off the rear leg, stepped back from in front
    const tech = this.technique;
    const t = sim.state.time - this.strikeAt;
    const span = this.span;
    let ahead = 0;
    switch (punch >= 0 && punch < PAD_HELD + RETRACT_T ? 'pad' : p.state) {
      case 'pad': {
        // The hips drive the knuckles to the pad's face: a pad too close stops
        // them with the arm still bent, one too far is a punch into the air
        const pad = this.pad!;
        const face = PAD_FACE + padOffset(pad.step);
        const out = (u: number): Pose => (pad.maai === 'far' ? whiffPose(u) : padPunch(u, face));
        if (punch < PAD_HELD) pose = out(punch);
        else if (pad.maai === 'in' && face >= strikeReach('gyakuZuki', undefined)) {
          pose = retractPose('gyakuZuki', 1 - (punch - PAD_HELD) / RETRACT_T);
        } else pose = swing(out(PAD_HELD), SHUKOKAI.kamae, smooth((punch - PAD_HELD) / RETRACT_T));
        break;
      }
      case 'hurt':
        pose = lunged(POSES.hurt, -0.1);
        break;
      default:
        if (this.whiffed && t < span.back) {
          // Too early: out into the air, held while the player is open, then back
          const reach = this.whiffReach(sim, p.side);
          pose =
            t < span.held
              ? whiffPose(t, reach)
              : swing(
                  whiffPose(span.held, reach),
                  SHUKOKAI.kamae,
                  smooth((t - span.held) / RETRACT_T),
                );
        } else if (t < span.held) pose = strikePose(tech, t, this.aim, this.lastContact);
        else if (t < span.back) pose = retractPose(tech, (span.back - t) / RETRACT_T, this.aim);
        else {
          // Back in kamae, bouncing lightly in the knees once the technique,
          // and off the rear leg its step back, is over
          const over = t - span.back - (this.stepsBack ? STEP_BACK_T : 0);
          pose = bounceDown(SHUKOKAI.kamae, this.time, smooth(over / BOUNCE_IN));
          if (over < 0) {
            const step = stepBack(pose, LAND_STEP, (t - span.back) / STEP_BACK_T);
            pose = step.pose;
            ahead = step.ahead;
          }
        }
    }
    let x = this.strikeSide * ahead * PLAYER_BUILD.scale;
    // A technique started while another still showed eases out of it
    const from = this.strikeFrom;
    if (from && from.facing === p.side && t < BLEND_T && p.state !== 'hurt') {
      const k = smooth(t / BLEND_T);
      pose = swing(from.pose, pose, k);
      x = from.x + (x - from.x) * k;
    }
    this.lastPlayer = { pose, x, facing: p.side };
    const blink = sim.state.invuln > 0 && Math.floor(sim.state.time * 14) % 2 === 0;
    return {
      x,
      y: 0,
      facing: p.side,
      pose,
      build: PLAYER_BUILD,
      fill: this.theme.ink,
      eyes: 'none',
      eyeColor: EYE_CALM,
      belt: this.beltColor,
      rim: this.theme.rim,
      rotation: 0,
      alpha: blink ? 0.45 : 1,
    };
  }

  /**
   * Whether the sim's last step moved `e`, by sim time: a frame with no sim
   * step since the last (hit-stop, pause, slow motion on a fast screen) keeps
   * the answer, so walkers don't drop into their waiting bounce for it.
   */
  private advancing(e: Enemy, time: number): boolean {
    const last = this.steps.get(e);
    if (last && time <= last.at) return last.moving;
    const moving = last ? Math.abs(e.d - last.d) > 1e-6 : true;
    this.steps.set(e, { d: e.d, at: time, moving });
    return moving;
  }

  /**
   * How much further off (sim units) `e` is drawn than the sim has it while
   * it walks freely: its footwork pulse taken out (`surgeAt`, but `PULSE_KEPT`
   * of it), so it walks at an even pace. Once the sim has it otherwise (held
   * in line, pressing in), that eases out.
   */
  private evened(e: Enemy, time: number): number {
    const phase = e.phase ?? e.id * 0.37;
    const c = e.stateT + phase;
    const surge = e.speed * (surgeAt(e.kind, c) - surgeAt(e.kind, phase));
    // Where the sim has it, walking freely since it set off
    const went = e.speed * e.stateT + surge;
    const began = time - e.stateT;
    let w = this.walks.get(e);
    if (!w || Math.abs(w.began - began) > T.dt / 2) {
      w = { began, d0: e.d + went, even: 1, t: time };
      this.walks.set(e, w);
    }
    const free = Math.abs(w.d0 - went - e.d) < 0.006;
    if (!free && time > w.t) w.even = Math.max(0, w.even - (time - w.t) / 0.2);
    w.t = time;
    return surge * (1 - PULSE_KEPT) * w.even;
  }

  /**
   * `pose` and `x`, eased into from how `e` was last drawn when it starts
   * moving a new way (`mode`), or the sim starts its state over (a windup held
   * while the karateka recovers from a hit), so nothing jumps between frames;
   * a strike landing snaps.
   */
  private eased(e: Enemy, mode: Mode, pose: Pose, x: number, time: number): [Pose, number] {
    let m = this.modes.get(e);
    if (!m || m.mode !== mode || (e.state === m.state && e.stateT < m.stateT)) {
      const from = m && !SNAPS.has(mode) ? m.last : null;
      const off = m && from ? m.lastX - x : 0;
      const f = this.feet.get(e);
      const held: [boolean, boolean] | null =
        m?.mode === 'meet' ? [!f?.posed(0), !f?.posed(1)] : null;
      m = {
        mode,
        at: time,
        state: e.state,
        stateT: e.stateT,
        from,
        held,
        off,
        last: pose,
        lastX: x,
      };
      this.modes.set(e, m);
    }
    m.state = e.state;
    m.stateT = e.stateT;
    const k = smooth((time - m.at) / (mode === 'recoil' || mode === 'dead' ? EASE_STRUCK : EASE_T));
    if (m.from && (k < 1 || (m.held && time - m.at < KICK_DOWN_T))) {
      // (out of standing still for a strike, on its feet, the feet on the
      // floor or stepping are the footwork's: where they stood, one that was
      // stepping set down; a kicking foot comes down with the pose)
      const eased = sweep(m.from, pose, k);
      if (m.held && mode !== 'dead') {
        // (the knee bent to it as the new pose's leg is shaped, its length
        // and the way it bends)
        // (LIMBS has the rear leg first, the footwork the lead foot)
        const held = [m.held[1], m.held[0]];
        const shaped = limbsOf(pose).map((l, i): Limb => (held[i] ? l : [l[0], l[1], NaN]));
        for (const [i, foot] of LEGS) if (m.held[i]) eased[foot] = pose[foot];
        Object.assign(eased, fit(eased, shaped));
        for (const [i, foot, knee] of LEGS) {
          if (!m.held[i] && m.from[foot][1] > GROUNDED) {
            // (a kick struck as it came in: the body rocks back at once, the
            // leg comes down after it)
            const leg = sweep(m.from, pose, smooth((time - m.at) / KICK_DOWN_T));
            for (const j of [knee, foot]) {
              eased[j] = [
                eased.hip[0] + leg[j][0] - leg.hip[0],
                eased.hip[1] + leg[j][1] - leg.hip[1],
              ];
            }
          }
        }
      }
      pose = eased;
      x += m.off * (1 - k);
    }
    m.last = pose;
    m.lastX = x;
    return [pose, x];
  }

  /**
   * Where `e` is drawn while knocked back (or for a moment after), given where
   * the sim has it (`x`): following it critically damped, so it slides back
   * from where a strike met it, carried on by a shove, and slows to a stop
   * before walking in again, never nearer the karateka than `clear` (a strike
   * still out).
   */
  private glide(e: Enemy, mode: Mode, x: number, time: number, clear: number): number {
    let g = this.glides.get(e);
    if (mode === 'meet' || mode === 'attack') {
      this.glides.delete(e);
      return x;
    }
    // (along the way it faces, from the karateka)
    const target = e.side * x;
    const knocked = e.state === 'recoil' || clear > 0;
    if (!g || time > g.until) {
      this.glides.delete(e);
      if (!knocked) return x;
      g = { x: e.side * (this.drawnX.get(e) ?? x), v: 0, t: time, until: time };
      this.glides.set(e, g);
    }
    if (knocked) g.until = time + GLIDE_T;
    const dt = time - g.t;
    const n = Math.ceil(dt * 240);
    for (let i = 0; i < n; i++) {
      const h = dt / n;
      g.v += (GLIDE_W * GLIDE_W * (target - g.x) - 2 * GLIDE_W * g.v) * h;
      g.x += g.v * h;
    }
    if (g.x < clear) {
      if (dt > 0) g.v = Math.max(g.v, Math.min(SHOVE_MAX, (clear - g.x) / dt));
      g.x = clear;
    }
    g.t = time;
    return e.side * (target + (g.x - target) * smooth((g.until - time) / GLIDE_T));
  }

  /**
   * `pose` (its body drawn at `x`) with the feet where they stand on the
   * floor: planted where they landed, stepping as the body goes on past them,
   * walking in each type's own gait on the sim's footwork pulse (footwork.ts).
   */
  private onFeet(e: Enemy, mode: Mode, pose: Pose, x: number, time: number, build: Build): Pose {
    const k = build.scale;
    // Along the way it faces (world units)
    const u = -e.side * x;
    const at = (p: Pose, foot: 'fFoot' | 'bFoot'): number => u + p[foot][0] * k;
    const walking = mode === 'walk';
    const own = guardOf(e);
    const gait = gaitOf(e.kind, e.boss !== undefined);
    const rhythm = rhythmFor(
      e.kind,
      e.boss !== undefined,
      e.speed,
      reachSpan(own, build, gait),
      legsOf(own, k),
    );
    // Off its feet; held as it was while a strike is on its way
    if (mode === 'dead') return pose;
    let f = this.feet.get(e);
    // (stepping in to meet a strike that would fall short, its feet step in
    // under it; else they stand as they were)
    const meet = this.meets.get(e);
    if (mode === 'meet' && meet && Math.abs(meet.to - meet.from) < STILL_MEET) {
      // (a foot only just off the floor is down)
      const held = (foot: 'fFoot' | 'bFoot'): [number, number] => [
        at(pose, foot),
        pose[foot][1] > GROUNDED ? pose[foot][1] * k : 0,
      ];
      f ??= new Feet(u, [at(pose, 'fFoot'), at(pose, 'bFoot')], time);
      this.feet.set(e, f);
      f.hold(u, [held('fFoot'), held('bFoot')], time);
      return pose;
    }
    const posed: [number, number] = [at(pose, 'fFoot'), at(pose, 'bFoot')];
    let spots = posed;
    // Walking, the feet pass under the body, upright all the way in (crouching
    // into the guard while still striding splayed the legs, right in front
    // of the karateka); stopping, they settle into the guard's stance
    if (walking) {
      const mid = (posed[0] + posed[1]) / 2;
      spots = [mid, mid];
    }
    const drive: Drive = {
      u,
      spots,
      own: posed,
      // The sim's footwork clock (sim.ts: its speed pulses with it)
      beat: walking ? rhythm.rate * (e.stateT + (e.phase ?? e.id * 0.37)) : null,
      period: 1 / rhythm.rate,
      gait: rhythm.gait,
      running: rhythm.running,
      scale: k,
      // A foot off the floor in the pose (a kick) is the pose's to move
      free: [pose.fFoot[1] > GROUNDED, pose.bFoot[1] > GROUNDED],
      heights: [pose.fFoot[1] * k, pose.bFoot[1] * k],
      reach: reachesOf(pose, build, gait),
      // (the wrestler presses in slower inside reach)
      ...(walking
        ? {
            pace: e.speed * (e.kind === 'grappler' && e.d <= T.reach ? GRAPPLE.press : 1),
            surge: rhythm.surge,
            pulse: rhythm.pulse,
          }
        : {}),
    };
    if (f) f.update(drive, time);
    else {
      // Coming onto the mat mid-stride
      f = walking ? Feet.walking(drive, time, e.speed) : new Feet(u, spots, time);
      this.feet.set(e, f);
    }
    // Steps a pose takes itself: the feinter's stamp out and back, the lead
    // foot going in with the attack thrown, landing as it lands
    const began = time - e.stateT;
    const cue = (name: string, foot: 0 | 1, to: number, dur: number, lift: number): void => {
      const key = `${name}@${began.toFixed(4)}`;
      if (f.cue === key) return;
      f.cue = key;
      f.step(foot, to, Math.max(0.03, dur), lift * k, time);
    };
    if (mode === 'feint' && e.kind === 'feinter') {
      if (e.stateT < FEINT_STAMP)
        cue('out', 0, u + STREET.feinterFeint.fFoot[0] * k, FEINT_STAMP - e.stateT, 0.05);
      else if (e.stateT < 2 * FEINT_STAMP)
        cue('back', 0, at(guardOf(e), 'fFoot'), 2 * FEINT_STAMP - e.stateT, 0.03);
    }
    if (mode === 'windup' && THROW_T[e.kind] > 0) {
      const left = e.windup - e.stateT;
      if (left < THROW_T[e.kind]) {
        // Where the body will be once thrown, the lead foot under it as the attack has it
        const lunged = u + LUNGE * (1 - smooth(1 - left / THROW_T[e.kind]));
        cue('throw', 0, lunged + ATTACK[e.kind].fFoot[0] * k, left, 0.05);
      }
    }
    return feetUnder(
      f,
      pose,
      u,
      build,
      time,
      rhythm.gait,
      f.walking(walking ? 1 : 0, time),
      rhythm.running,
    );
  }

  private enemyFigure(e: Enemy, time: number): Figure {
    const theme = this.theme;
    // The two-hit types look hurt after the first hit; every type moves in its own way
    // A boss's outfit is a chunk of its own (a plain gi until it's in)
    if (e.boss) void loadBossLooks();
    const hurt = !e.boss && e.hp < KINDS[e.kind].hp ? HURT[e.kind] : undefined;
    const build = buildOf(e);
    const guard = idle(e.kind, hurt ?? guardOf(e), time + e.id);
    let pose: Pose;
    let rotation = 0;
    let x = e.side * drawnDistance(e.d);
    let y = 0;
    let alpha = 1;
    let eyes: Eyes = 'calm';
    let eyeColor = EYE_CALM;
    let rim = theme.rim;
    const moving = this.advancing(e, time);
    if (e.state === 'walk' && moving) x = e.side * drawnDistance(e.d + this.evened(e, time));
    const attack = this.attacks.get(e);
    const attacked = attack ? (time - attack.at) / ATTACK_T : 1;
    const struck = e.state === 'dead' || e.state === 'recoil';
    // The strike lands on it this long after the hit, whether or not it's
    // still reeling in the sim by then (the wrestler barely is)
    const hold = this.contact.get(e) ?? 0;
    let meet = this.meets.get(e);
    const since = meet ? time - meet.at : 0;
    // Kept till the strike is pulled back
    if (meet && !struck && since >= Math.max(strikeSpan(meet.tech).held, hold)) {
      this.meets.delete(e);
      meet = undefined;
    }
    const meeting = meet !== undefined && since < hold;
    let mode: Mode;

    if (meet && meeting) {
      // Until the strike lands it stands as it was, settling to where the strike meets it
      const u = since / hold;
      mode = 'meet';
      pose = meet.pose;
      x = meet.from + (meet.to - meet.from) * u * u * (3 - 2 * u);
      eyes = meet.eyes;
      eyeColor = meet.eyeColor;
    } else if (attack && attacked < 1 && e.state !== 'dead') {
      // The attack that just hit the player (thrown as its windup ran out):
      // held, lunged from where it stood across the stand-off to reach the
      // karateka, then back to its guard where the sim has backed it off to
      const lunge = attack.x - e.side * LUNGE;
      const back = smooth((attacked - ATTACK_HELD) / (1 - ATTACK_HELD));
      mode = 'attack';
      pose = back > 0 ? sweep(ATTACK[e.kind], guard, back) : ATTACK[e.kind];
      x = lunge + (x - lunge) * back;
      eyes = 'angry';
      eyeColor = C.danger;
    } else {
      mode = e.state;
      switch (e.state) {
        case 'walk':
          // Stepping in behind the guard (the feet: onFeet); waiting in line,
          // they bounce on the spot
          if (moving) pose = guard;
          else {
            mode = 'stand';
            pose = bounceDown(guard, time + e.id);
          }
          break;
        case 'feint': {
          // A fake to draw the early strike: the feinter stamps in with his
          // hands still low (they only come up for the real rush)
          const k = Math.sin(Math.min(1, e.stateT / 0.22) * Math.PI);
          pose =
            e.kind === 'feinter'
              ? sweep(guard, STREET.feinterFeint, k)
              : sweep(guard, ATTACK[e.kind], 0.55 * k);
          break;
        }
        case 'windup': {
          // Builds over the whole windup, then trembles just before release,
          // the feet staying put; thrown as it runs out, so the blow lands on
          // the karateka just as the sim has it hit (a strike in time catches
          // it on the way: a counter). The kickboxer's kick flies in after.
          const k = Math.min(1, e.stateT / e.windup);
          const ease = 1 - (1 - Math.min(1, k * 1.4)) ** 2;
          pose = sweep(guard, WINDUP[e.kind], ease);
          if (k > 0.7)
            pose = fit(nudge(pose, UPPER, Math.sin(time * 90) * 0.012, 0), limbsOf(pose));
          const throws = THROW_T[e.kind];
          const thrown = throws > 0 ? smooth(1 - (e.windup - e.stateT) / throws) : 0;
          if (thrown > 0) {
            pose = sweep(pose, ATTACK[e.kind], thrown);
            x -= e.side * LUNGE * thrown;
          }
          eyes = 'angry';
          eyeColor = C.danger;
          rim = Math.floor(time * 16) % 2 === 0 ? C.danger : '#ff9a6b';
          break;
        }
        case 'launch': {
          // The kick flies in: hop forward as the leg unfolds
          // The knee stays up and leads, the foot sweeps over it last
          const k = Math.min(1, e.stateT / 0.22);
          pose = kickExtend(STREET.kickerWindup, STREET.kickerAttack, k, 'arc');
          x -= e.side * LUNGE * smooth(e.stateT / LAUNCH_T);
          eyes = 'angry';
          eyeColor = C.danger;
          rim = C.danger;
          break;
        }
        case 'recoil': {
          // (those the sim steps after the attacker have a step more on the clock)
          if (Math.abs(time - e.stateT - this.hurtAt) < T.dt * 1.5) {
            // Backing off as the karateka took a hit: on guard, not struck
            mode = 'back';
            pose = guard;
            break;
          }
          // Knocked back on guard, reeling from the strike (below)
          pose = guard;
          break;
        }
        case 'dead': {
          // Lifted off the feet and tipping back, dropped on the back, sliding
          // to a stop, all from the moment and the place the strike met it
          const tt = e.stateT - hold;
          eyes = 'hurt';
          if (meet) x = meet.to;
          if (tt < AIRBORNE) {
            const up = Math.max(0, tt / AIRBORNE);
            const fall = knockedDown(STAGGER[e.kind], up);
            pose = fall.pose;
            rotation = fall.rotation;
            y = fall.lift;
            x += e.side * up * 0.9;
          } else {
            // Down on its back with a little bounce, sliding to a stop
            const k = Math.min(1, (tt - AIRBORNE) / (T.deadTime - hold - AIRBORNE));
            const bounce = (tt - AIRBORNE) / BOUNCE_T;
            pose = STREET.down;
            y = bounce < 1 ? BOUNCE * Math.sin(bounce * Math.PI) : 0;
            x += e.side * (0.9 + (1 - (1 - k) ** 2) * 0.45);
            alpha = k < 0.6 ? 1 : 1 - (k - 0.6) / 0.4;
          }
          // A strike still going out shoves it on ahead of its flight
          if (meet) {
            meet.shove = Math.max(meet.shove, clearOf(meet, since) - Math.abs(x));
            x += e.side * meet.shove;
          }
          break;
        }
      }
    }
    // Reeling from a strike that reached it, whatever it does meanwhile
    const reel = this.reels.get(e);
    if (reel && mode !== 'meet' && mode !== 'dead' && mode !== 'attack' && mode !== 'back') {
      const a = reeling(reel, time);
      if (a > 0) {
        pose = sweep(pose, STAGGER[e.kind], a);
        // (the wrestler only angrier, the boxer behind his gloves unmoved)
        if (a > 0.25 && mode !== 'windup' && mode !== 'launch') {
          if (e.kind === 'grappler') eyes = 'angry';
          else if (reel.amount > 0.5) eyes = 'hurt';
        }
      } else if (time > reel.at) this.reels.delete(e);
    }
    // Knocked back, it slides from where the strike met it and slows to a stop
    // before walking in again, never inside the strike while it's out
    if (mode !== 'dead')
      x = this.glide(e, mode, x, time, meet && !meeting ? clearOf(meet, since) : 0);
    [pose, x] = this.eased(e, mode, pose, x, time);
    pose = this.onFeet(e, mode, pose, x, time, build);
    // Fade in at the spawn edge so wide screens don't show a pop
    alpha *= Math.min(1, (T.spawnDistance + 0.1 - e.d) / 0.4);
    // What a strike now would meet (held still while one is on its way)
    if (!meeting) this.shown.set(e, { pose, x, eyes, eyeColor });
    this.drawnX.set(e, x);
    return {
      x,
      y,
      facing: -e.side as 1 | -1,
      pose,
      build,
      fill: this.flashes.has(e) ? '#ffffff' : theme.ink,
      eyes,
      eyeColor,
      rim,
      rotation,
      alpha: Math.max(0, alpha),
    };
  }

  /** Render a figure into pooled offscreen canvases (shape + full). */
  private renderSprite(f: Figure, index: number): Sprite {
    const { ppu, dpr } = this;
    const w = Math.ceil(SPRITE_W * ppu * dpr);
    const h = Math.ceil(SPRITE_H * ppu * dpr);
    let sp = this.sprites[index];
    if (!sp) {
      sp = {
        shape: document.createElement('canvas'),
        full: document.createElement('canvas'),
        lit: document.createElement('canvas'),
        halo: document.createElement('canvas'),
      };
      this.sprites[index] = sp;
    }
    for (const c of [sp.shape, sp.full, sp.lit]) {
      if (c.width !== w || c.height !== h) {
        c.width = w;
        c.height = h;
      }
    }
    const paint = {
      fill: f.fill,
      ...(f.belt ? { belt: f.belt } : {}),
      eyes: f.eyes,
      eyeColor: f.eyeColor,
      time: this.time,
    };
    const feetX = (SPRITE_W / 2) * ppu;
    const feetY = (SPRITE_H - SPRITE_FEET) * ppu;
    const full = sp.full.getContext('2d');
    const shape = sp.shape.getContext('2d');
    // Blur for free: bilinear upscaling of a tiny copy (same softness at any DPR)
    const haloScale = Math.max(2, Math.round(3 * dpr));
    const hw = Math.ceil(w / haloScale);
    const hh = Math.ceil(h / haloScale);
    if (sp.halo.width !== hw || sp.halo.height !== hh) {
      sp.halo.width = hw;
      sp.halo.height = hh;
    }
    const lit = sp.lit.getContext('2d');
    const halo = sp.halo.getContext('2d');
    if (!full || !shape || !lit || !halo) return sp;
    // Solid body first…
    full.setTransform(dpr, 0, 0, dpr, 0, 0);
    full.clearRect(0, 0, w, h);
    drawFighter(full, feetX, feetY, ppu, f.facing, f.pose, f.build, paint, {
      rotation: f.rotation,
      parts: 'body',
    });
    // …copied and tinted to the rim colour…
    shape.setTransform(1, 0, 0, 1, 0, 0);
    shape.clearRect(0, 0, w, h);
    shape.globalCompositeOperation = 'source-over';
    shape.drawImage(sp.full, 0, 0);
    shape.globalCompositeOperation = 'source-in';
    shape.fillStyle = f.rim;
    shape.fillRect(0, 0, w, h);
    shape.globalCompositeOperation = 'source-over';
    // …then belt and eyes on the full sprite
    drawFighter(full, feetX, feetY, ppu, f.facing, f.pose, f.build, paint, {
      rotation: f.rotation,
      parts: 'details',
    });
    // Backlight: the rim shows on the sides facing the light source, over a
    // soft halo while the quality governor allows it. Right in front of the
    // light a fighter is rimmed on both sides; moving off to one side, the far
    // side's rim slides in behind the body, so it never jumps across
    const r = this.rimPx();
    const body = this.sx(f.x) + f.facing * f.pose.hip[0] * f.build.scale * this.ppu;
    const toward = Math.max(
      -1,
      Math.min(1, (this.w * this.theme.lightX - body) / (RIM_RAMP * this.ppu)),
    );
    lit.setTransform(1, 0, 0, 1, 0, 0);
    lit.clearRect(0, 0, w, h);
    if (this.maxDpr >= 2) {
      halo.clearRect(0, 0, hw, hh);
      // Filtered shrink (small target, so cheap); a plain one aliases into stair-steps
      halo.imageSmoothingQuality = 'medium';
      halo.drawImage(sp.shape, 0, 0, hw, hh);
      lit.globalAlpha = 0.55;
      lit.drawImage(sp.halo, toward * r * 2 * dpr, -r * 2 * dpr, hw * haloScale, hh * haloScale);
      lit.globalAlpha = 1;
    }
    lit.drawImage(sp.shape, r * Math.min(1, 1 + toward) * dpr, -r * 1.1 * dpr);
    lit.drawImage(sp.shape, -r * Math.min(1, 1 - toward) * dpr, -r * 1.1 * dpr);
    lit.drawImage(sp.full, 0, 0);
    return sp;
  }

  /** Width of the backlit rim (CSS px), scaled with the fighters. */
  private rimPx(): number {
    return Math.max(1.6, this.ppu * 0.02);
  }

  /** Where a figure's sprite goes on screen: x, y, width, height. */
  private spriteRect(f: Figure): [number, number, number, number] {
    const { ppu } = this;
    return [
      this.sx(f.x) - (SPRITE_W / 2) * ppu,
      this.sy(f.y) - (SPRITE_H - SPRITE_FEET) * ppu,
      SPRITE_W * ppu,
      SPRITE_H * ppu,
    ];
  }

  private drawFigure(f: Figure, sp: Sprite): void {
    const { ctx } = this;
    const [x, y, w, h] = this.spriteRect(f);
    ctx.globalAlpha = f.alpha;
    ctx.drawImage(sp.lit, x, y, w, h);
    ctx.globalAlpha = 1;
  }

  /** Faint mirrored fighters on polished floors, fading with depth. */
  private drawReflections(figures: Figure[], sprites: Sprite[]): void {
    const { ctx, ppu, dpr } = this;
    const depth = Math.min(this.h - this.groundY, ppu * 2.2);
    const layer = (this.floorLayer ??= document.createElement('canvas'));
    const lw = Math.ceil(this.w * dpr);
    const lh = Math.ceil(depth * dpr);
    if (layer.width !== lw || layer.height !== lh) {
      layer.width = lw;
      layer.height = lh;
    }
    const fl = layer.getContext('2d');
    if (!fl) return;
    fl.setTransform(dpr, 0, 0, dpr, 0, 0);
    fl.clearRect(0, 0, this.w, depth);
    figures.forEach((f, i) => {
      const sp = sprites[i];
      if (!sp) return;
      const [x, , w, h] = this.spriteRect(f);
      fl.globalAlpha = f.alpha * 0.24;
      fl.save();
      // Mirror around the floor line, as far below it as the figure is above
      fl.translate(0, f.y * ppu);
      fl.scale(1, -1);
      fl.drawImage(sp.full, x, -(SPRITE_H - SPRITE_FEET) * ppu, w, h);
      fl.restore();
    });
    fl.globalAlpha = 1;
    fl.globalCompositeOperation = 'destination-out';
    const fade = fl.createLinearGradient(0, 0, 0, depth);
    fade.addColorStop(0, 'rgba(0,0,0,0)');
    fade.addColorStop(1, 'rgba(0,0,0,1)');
    fl.fillStyle = fade;
    fl.fillRect(0, 0, this.w, depth);
    fl.globalCompositeOperation = 'source-over';
    ctx.drawImage(layer, 0, this.groundY, this.w, depth);
  }

  /** Windup ring and the heavy's hit pips. */
  private drawEnemyMarks(e: Enemy, time: number): void {
    const scale = buildOf(e).scale;
    const x = this.drawnX.get(e) ?? e.side * drawnDistance(e.d);
    // Hits left: on the two-hit types, and on a boss all the way down; a hit
    // counts once the strike gets there
    const hp = e.hp + (this.incoming(e, time) ? 1 : 0);
    const pips = (e.boss || ((e.kind === 'heavy' || e.kind === 'grappler') && hp > 1)) && hp > 0;
    const pipY = 1.95 * scale + (e.boss ? 0.12 : 0);
    if (pips) this.drawPip(x, pipY, hp);
    // A windup's "!" sits above the dots, never with them inside its ring
    const ring = pips ? pipY + 0.4 : 1.9 * scale;
    if (e.state === 'windup') this.drawTelegraph(x, ring, e.stateT / e.windup);
    // The coach's word on an exam's new opponent type, riding above it (and
    // above where the ring comes, so it doesn't jump when a windup starts)
    if (this.coach.label === e) {
      const { ctx, tu } = this;
      ctx.font = `800 ${Math.round(tu * 0.26)}px system-ui, sans-serif`;
      const text = t(`coach.${e.kind as Exclude<EnemyKind, 'basic'>}`);
      const half = ctx.measureText(text).width / 2 + tu * 0.12;
      const cx = Math.max(half, Math.min(this.w - half, this.sx(x)));
      this.outlined(text, cx, this.sy(ring + TELEGRAPH_R + 0.2), C.gold);
    }
  }

  /** Whether a strike is still on its way to opponent `e`. */
  private incoming(e: Enemy, time: number): boolean {
    const meet = this.meets.get(e);
    return meet !== undefined && time - meet.at < (this.contact.get(e) ?? 0);
  }

  /** Test hooks: where opponent `e` was last drawn (world x), and when a strike last met it. */
  drawnAt(e: Enemy): number | undefined {
    return this.drawnX.get(e);
  }
  metAt(e: Enemy): number | undefined {
    return this.meets.get(e)?.at;
  }

  /** A white arc sweeping out from the player along the strike. */
  private drawSlash(sl: Slash, player: Figure): void {
    const { ctx, ppu } = this;
    const k = sl.t / sl.life;
    const cx = this.sx(player.x);
    const cy = this.sy(sl.height - (sl.kick ? 0.1 : 0.05));
    const r = ppu * (sl.kick ? 0.78 : 0.72);
    // Kicks sweep up in a wide arc, punches are a flat streak
    const span = sl.kick ? 1.3 : 0.35;
    const mid = sl.side > 0 ? 0 : Math.PI;
    const dir = sl.side > 0 ? -1 : 1;
    const a0 = mid - (dir * span) / 2;
    const head = a0 + dir * span * Math.min(1, k / SLASH_SWEEP);
    ctx.save();
    ctx.globalAlpha = 1 - k;
    ctx.strokeStyle = '#ffffff';
    ctx.lineCap = 'round';
    const steps = 10;
    for (let i = 0; i < steps; i++) {
      const u0 = a0 + ((head - a0) * i) / steps;
      const u1 = a0 + ((head - a0) * (i + 1)) / steps;
      ctx.lineWidth = ppu * 0.09 * ((i + 1) / steps);
      ctx.beginPath();
      ctx.arc(cx, cy, r, Math.min(u0, u1), Math.max(u0, u1));
      ctx.stroke();
    }
    ctx.restore();
  }

  private drawImpact(im: Impact): void {
    const { ctx, ppu } = this;
    const k = im.t / im.life;
    const r = im.size * ppu * (0.6 + k * 0.6);
    const cx = this.sx(im.x);
    const cy = this.sy(im.y);
    // A short burst of light behind the star
    const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, r * 2.2);
    glow.addColorStop(0, im.color);
    glow.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.globalAlpha = (1 - k) * 0.45;
    ctx.fillStyle = glow;
    ctx.fillRect(cx - r * 2.2, cy - r * 2.2, r * 4.4, r * 4.4);
    ctx.globalAlpha = 1 - k;
    ctx.fillStyle = im.color;
    ctx.beginPath();
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2 + im.x;
      const rr = i % 2 === 0 ? r : r * 0.38;
      const px = cx + Math.cos(a) * rr;
      const py = cy + Math.sin(a) * rr;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  private drawShadow(x: number, r: number, alpha: number): void {
    const { ctx, ppu } = this;
    ctx.globalAlpha = alpha;
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    ctx.beginPath();
    ctx.ellipse(this.sx(x), this.groundY + ppu * 0.02, r * ppu, r * ppu * 0.14, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  /** The windup's "!" in a ring that fills, centred at height `y`. */
  private drawTelegraph(x: number, y: number, progress: number): void {
    const { ctx, ppu } = this;
    const cx = this.sx(x);
    const cy = this.sy(y);
    ctx.fillStyle = C.danger;
    ctx.font = `900 ${Math.round(ppu * 0.5)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('!', cx, cy);
    ctx.strokeStyle = C.danger;
    ctx.lineWidth = ppu * 0.05;
    ctx.beginPath();
    const end = -Math.PI / 2 + Math.PI * 2 * Math.min(1, progress);
    ctx.arc(cx, cy, ppu * TELEGRAPH_R, -Math.PI / 2, end);
    ctx.stroke();
  }

  private drawPip(x: number, y: number, hp: number): void {
    const { ctx, ppu } = this;
    ctx.fillStyle = C.text;
    for (let i = 0; i < hp; i++) {
      ctx.beginPath();
      const gap = hp > 3 ? 0.15 : 0.2;
      ctx.arc(this.sx(x) + (i - (hp - 1) / 2) * ppu * gap, this.sy(y), ppu * 0.06, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private drawBanner(b: { title: string; sub: string; t: number; life: number }): void {
    const { ctx, w, tu } = this;
    const k = b.t / b.life;
    const alpha = k < 0.12 ? k / 0.12 : k > 0.8 ? (1 - k) / 0.2 : 1;
    const pop = k < 0.12 ? 0.8 + (k / 0.12) * 0.2 : 1;
    let cy = this.sy(2.15);
    // The impact test's call stays off its meter: above it where the screen
    // has room (portrait), else on the mat below (the Kiai bar is away then)
    if (this.pad) {
      const above = this.sy(METER_TOP) - tu * 0.8;
      if (above - tu * 0.65 >= HUD_H) cy = above;
      else if (this.h - this.groundY >= tu * 1.05) cy = (this.groundY + this.h) / 2;
    }
    ctx.globalAlpha = alpha;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // No backdrop: a solid ink outline, as on the floating calls, reads on the
    // white dojo wall and at night alike and leaves the fight in view
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(20,14,18,0.9)';
    // The Karate face only has ASCII: Japanese calls use it, translations don't
    const jp = /^[\x20-\x7e]*$/.test(b.title);
    ctx.font = jp
      ? `${Math.round(tu * 0.62 * pop)}px Karate, system-ui, sans-serif`
      : `900 ${Math.round(tu * 0.5 * pop)}px system-ui, sans-serif`;
    ctx.lineWidth = tu * 0.1;
    ctx.strokeText(b.title, w / 2, cy - tu * 0.14, w - 24);
    ctx.fillStyle = C.gold;
    ctx.fillText(b.title, w / 2, cy - tu * 0.14, w - 24);
    // Shrink long (translated) hints to fit instead of running off the screen
    const sub = b.sub;
    let size = Math.round(tu * 0.24);
    ctx.font = `800 ${size}px system-ui, sans-serif`;
    const fit = (w - 32) / ctx.measureText(sub).width;
    if (fit < 1) {
      size = Math.max(10, Math.floor(size * fit));
      ctx.font = `800 ${size}px system-ui, sans-serif`;
    }
    ctx.lineWidth = tu * 0.07;
    ctx.strokeText(sub, w / 2, cy + tu * 0.36);
    ctx.fillStyle = C.text;
    ctx.fillText(sub, w / 2, cy + tu * 0.36);
    ctx.globalAlpha = 1;
  }

  private drawFloater(f: Floater): void {
    const { ctx, tu } = this;
    const k = f.t / f.life;
    // Pops in past full size and settles back (ease-out-back)
    const u = Math.min(1, k / 0.18) - 1;
    const pop = Math.max(0.3, 1 + 2.70158 * u ** 3 + 1.70158 * u ** 2);
    const x = Math.max(-1.9, Math.min(1.9, f.x));
    const cy = this.sy(f.y + k * (f.rise ?? 0.5));
    ctx.globalAlpha = k > 0.7 ? (1 - k) / 0.3 : 1;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `900 ${Math.round(tu * f.size * pop)}px system-ui, sans-serif`;
    // Long calls ("PERFECT ×12") stay on screen over an opponent at the edge
    const half = ctx.measureText(f.text).width / 2 + tu * 0.12;
    const cx = half * 2 < this.w ? Math.max(half, Math.min(this.w - half, this.sx(x))) : this.w / 2;
    // A solid ink outline: gold must read on the white dojo wall and at dusk alike
    ctx.lineJoin = 'round';
    ctx.lineWidth = tu * 0.1;
    ctx.strokeStyle = 'rgba(20,14,18,0.9)';
    // Wider than the screen ("70× PERFEKT in Folge!" on a narrow phone): squeezed to fit
    const fit = this.w - tu * 0.24;
    ctx.strokeText(f.text, cx, cy, fit);
    ctx.fillStyle = f.color;
    ctx.fillText(f.text, cx, cy, fit);
    if (f.sub) {
      ctx.font = `700 ${Math.round(tu * 0.2)}px system-ui, sans-serif`;
      ctx.lineWidth = tu * 0.07;
      ctx.strokeText(f.sub, cx, cy + tu * 0.36);
      ctx.fillStyle = C.text;
      ctx.fillText(f.sub, cx, cy + tu * 0.36);
    }
    ctx.globalAlpha = 1;
  }
}
